import React, { useState, useEffect, useMemo } from "react";
import "./GestioneDocumenti.css";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { useAuthContext } from "../../hooks/useAuthContext";
import { format } from "date-fns";
import Select from "react-select";
import { downloadConsuntivoPdf } from "../../stampe/generatori/consuntivoPdf";
import { database, timestamp } from "../../firebase/config";

const getMaterialiXY = (doc) => {
  const items = Array.isArray(doc?.materiale_utilizzato)
    ? doc.materiale_utilizzato
    : Array.isArray(doc?.elenco_articoli)
      ? doc.elenco_articoli
      : [];
  const y = items.length;
  const x = items.reduce((acc, it) => {
    const pu = it?.prezzo_unitario;
    if (typeof pu === "number") return acc + (isNaN(pu) ? 0 : 1);
    if (pu == null) return acc;
    if (typeof pu === "string") return acc + (pu.trim() === "" ? 0 : 1);
    return acc + 1;
  }, 0);
  return { x, y };
};

const hasMaterialiNonValorizzati = (doc) => {
  const { x, y } = getMaterialiXY(doc);
  return x !== y;
};

const toNumberLoose = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const raw = String(value).trim();
  if (!raw) return NaN;
  let normalized = raw.replace(/€/g, "").replace(/\s+/g, "");
  const hasDot = normalized.includes(".");
  const hasComma = normalized.includes(",");

  if (hasDot && hasComma) {
    const lastDot = normalized.lastIndexOf(".");
    const lastComma = normalized.lastIndexOf(",");
    if (lastComma > lastDot) {
      normalized = normalized.replace(/\./g, "").replace(/,/g, ".");
    } else {
      normalized = normalized.replace(/,/g, "");
    }
  } else if (hasComma) {
    normalized = normalized.replace(/,/g, ".");
  }

  normalized = normalized.replace(/[^0-9.-]/g, "");
  const n = parseFloat(normalized);
  return isNaN(n) ? NaN : n;
};

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const parseDateStringLoose = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map((x) => Number(x));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    return { y, m, d };
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [d, m, y] = raw.split("/").map((x) => Number(x));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    return { y, m, d };
  }

  return null;
};

const parseTimeToMinutesLoose = (value) => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const match = raw.match(/^(\d{1,2})\s*[:.,]\s*(\d{1,2})$/);
  if (match) {
    const hh = Number(match[1]);
    const mm = Number(match[2]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }

  const matchHoursOnly = raw.match(/^(\d{1,2})$/);
  if (matchHoursOnly) {
    const hh = Number(matchHoursOnly[1]);
    if (!Number.isFinite(hh) || hh < 0 || hh > 23) return null;
    return hh * 60;
  }

  return null;
};

const buildLocalDateTimeFromLoose = (dateStr, timeStr) => {
  const dateParts = parseDateStringLoose(dateStr);
  const minutes = parseTimeToMinutesLoose(timeStr);
  if (!dateParts || minutes == null) return null;
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return new Date(dateParts.y, dateParts.m - 1, dateParts.d, hh, mm, 0, 0);
};

const splitStraordinarioOrdinarieHours = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    return { oreOrdinarie: 0, oreStraordinarie: 0 };
  }
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  ) {
    return { oreOrdinarie: 0, oreStraordinarie: 0 };
  }

  const overlapMinutes = (aStart, aEnd, bStart, bEnd) =>
    Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));

  let ordMinutes = 0;
  let strMinutes = 0;

  let cursor = new Date(startMs);
  while (cursor.getTime() < endMs) {
    const nextMidnight = new Date(cursor.getTime());
    nextMidnight.setHours(24, 0, 0, 0);

    const segmentEndMs = Math.min(endMs, nextMidnight.getTime());
    const segmentMinutes = (segmentEndMs - cursor.getTime()) / (1000 * 60);
    if (segmentMinutes <= 0) break;

    const day = cursor.getDay();
    const isWeekend = day === 0 || day === 6;

    if (isWeekend) {
      strMinutes += segmentMinutes;
    } else {
      const startMin = cursor.getHours() * 60 + cursor.getMinutes();
      const endMin =
        segmentEndMs === nextMidnight.getTime()
          ? 1440
          : new Date(segmentEndMs).getHours() * 60 +
            new Date(segmentEndMs).getMinutes();

      const nightA = overlapMinutes(startMin, endMin, 0, 6 * 60);
      const nightB = overlapMinutes(startMin, endMin, 19 * 60, 24 * 60);
      const overtime = nightA + nightB;
      strMinutes += overtime;
      ordMinutes += segmentMinutes - overtime;
    }

    cursor = new Date(segmentEndMs);
  }

  return {
    oreOrdinarie: ordMinutes / 60,
    oreStraordinarie: strMinutes / 60,
  };
};

const getDocManodoperaBreakdown = (doc) => {
  if (!doc) {
    return { oreOrdinarie: 0, oreStraordinarie: 0, mode: "empty" };
  }

  if (doc.manodopera_breakdown_inizializzato === true) {
    const ord = toNumberLoose(doc?.totale_manodopera_ore);
    const str = toNumberLoose(doc?.totale_manodopera_straordinaria_ore);
    return {
      oreOrdinarie: Number.isFinite(ord) && ord > 0 ? ord : 0,
      oreStraordinarie: Number.isFinite(str) && str > 0 ? str : 0,
      mode: "stored_init",
    };
  }

  const hasExplicitStra =
    doc.totale_manodopera_straordinaria_ore != null &&
    doc.totale_manodopera_straordinaria_ore !== "";

  if (hasExplicitStra) {
    const ord = toNumberLoose(doc?.totale_manodopera_ore);
    const str = toNumberLoose(doc?.totale_manodopera_straordinaria_ore);
    return {
      oreOrdinarie: Number.isFinite(ord) && ord > 0 ? ord : 0,
      oreStraordinarie: Number.isFinite(str) && str > 0 ? str : 0,
      mode: "stored",
    };
  }

  const items = Array.isArray(doc?.manodopera) ? doc.manodopera : [];
  const fallbackDateStr = doc?.data_documento || doc?.data || "";

  let ord = 0;
  let str = 0;
  let didComputeSplit = false;

  items.forEach((m) => {
    const totaleOre = toNumberLoose(m?.totale_ore);
    if (!Number.isFinite(totaleOre) || totaleOre <= 0) return;

    const rowDateStr = m?.giorno_intervento || m?.giorno || fallbackDateStr;
    const start = buildLocalDateTimeFromLoose(rowDateStr, m?.ora_inizio);
    const endRaw = buildLocalDateTimeFromLoose(rowDateStr, m?.ora_fine);

    const oreViaggio = toNumberLoose(m?.ore_viaggio);
    const travel =
      Number.isFinite(oreViaggio) && oreViaggio > 0 ? oreViaggio : 0;
    const baseWork = Math.max(0, totaleOre - travel);

    if (start && endRaw) {
      const end =
        endRaw.getTime() <= start.getTime()
          ? new Date(endRaw.getTime() + 24 * 60 * 60 * 1000)
          : endRaw;

      const durationHours =
        (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      if (Number.isFinite(durationHours) && durationHours > 0) {
        const splitDuration = splitStraordinarioOrdinarieHours(start, end);
        const overtimeFraction = Math.min(
          1,
          Math.max(0, splitDuration.oreStraordinarie / durationHours),
        );

        const strWork = baseWork * overtimeFraction;
        const ordWork = baseWork - strWork;

        const travelIsWeekend = start.getDay() === 0 || start.getDay() === 6;
        const strTravel = travelIsWeekend ? travel : 0;
        const ordTravel = travelIsWeekend ? 0 : travel;

        ord += ordWork + ordTravel;
        str += strWork + strTravel;
        didComputeSplit = true;
        return;
      }
    }

    const dateParts = parseDateStringLoose(rowDateStr);
    if (dateParts) {
      const d = new Date(
        dateParts.y,
        dateParts.m - 1,
        dateParts.d,
        12,
        0,
        0,
        0,
      );
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      if (isWeekend) {
        str += totaleOre;
        didComputeSplit = true;
        return;
      }
    }

    ord += totaleOre;
  });

  if (didComputeSplit) {
    return {
      oreOrdinarie: round2(ord),
      oreStraordinarie: round2(str),
      mode: "computed",
    };
  }

  const legacyOre = toNumberLoose(doc?.totale_manodopera_ore);
  if (Number.isFinite(legacyOre) && legacyOre > 0) {
    return { oreOrdinarie: legacyOre, oreStraordinarie: 0, mode: "legacy" };
  }

  const sumFallback = items.reduce((acc, m) => {
    const o = toNumberLoose(m?.totale_ore);
    return acc + (Number.isFinite(o) ? o : 0);
  }, 0);
  return {
    oreOrdinarie: round2(sumFallback),
    oreStraordinarie: 0,
    mode: "fallback_sum",
  };
};

const hasStoredManodoperaBreakdown = (doc) => {
  if (!doc) return false;
  if (doc.manodopera_breakdown_inizializzato === true) return true;
  return (
    doc.totale_manodopera_straordinaria_ore != null ||
    doc.tariffa_manodopera_straordinaria != null
  );
};

const getManodoperaXY = (doc) => {
  const breakdown = getDocManodoperaBreakdown(doc);
  const oreOrdinarie = breakdown?.oreOrdinarie ?? 0;
  const oreStraordinarie = breakdown?.oreStraordinarie ?? 0;
  const y = oreOrdinarie > 0 || oreStraordinarie > 0 ? 1 : 0;

  const tariffaRaw = doc?.tariffa_manodopera;
  const tariffa = toNumberLoose(tariffaRaw);
  const tariffaStraRaw = doc?.tariffa_manodopera_straordinaria;
  const tariffaStra = toNumberLoose(tariffaStraRaw);

  const okOrd =
    oreOrdinarie <= 0 ||
    (!isNaN(tariffa) && Number.isFinite(tariffa) && tariffa > 0);
  const okStra =
    oreStraordinarie <= 0 ||
    (!isNaN(tariffaStra) && Number.isFinite(tariffaStra) && tariffaStra > 0);

  const x = y === 1 && okOrd && okStra ? 1 : 0;
  return { x, y };
};

const hasManodoperaNonValorizzata = (doc) => {
  const { x, y } = getManodoperaXY(doc);
  return x !== y;
};

const DOC_TYPES = {
  bolla_acquisto: {
    label: "Bolle di Acquisto",
    collection: "bolle_acquisto",
    fields: [
      { key: "numero_documento", label: "Documento", type: "text" },
      { key: "data_documento", label: "Data", type: "date" },
      { key: "fornitore", label: "Fornitore", type: "text" },
      { key: "importo_totale", label: "Importo Totale", type: "number" },
    ],
  },
  bolla_vendita: {
    label: "Bolle di Vendita",
    collection: "bolle_vendita",
    fields: [
      { key: "numero_documento", label: "Documento", type: "text" },
      { key: "data_documento", label: "Data", type: "date" },
      { key: "cliente", label: "Cliente", type: "text" },
      { key: "luogo_intervento", label: "Luogo Intervento", type: "text" },
      {
        key: "oggetto_intervento",
        label: "Oggetto Intervento",
        type: "textarea",
      },
      {
        key: "__materiali_quantificati",
        label: "Materiali",
        type: "text",
        sortable: false,
      },
      {
        key: "__manodopera_quantificata",
        label: "Manodopera",
        type: "text",
        sortable: false,
      },
    ],
  },
  generico: {
    label: "Documenti Generici",
    collection: "documenti_generici",
    fields: [
      { key: "tipo_documento_specifico", label: "Tipo", type: "text" }, // rinominato per evitare conflitto
      { key: "data", label: "Data", type: "text" },
      { key: "mittente", label: "Mittente", type: "text" },
      { key: "destinatario", label: "Destinatario", type: "text" },
      { key: "oggetto", label: "Oggetto", type: "textarea" },
      { key: "importi", label: "Importi", type: "text" },
    ],
  },
};

export default function GestioneDocumenti() {
  const { user } = useAuthContext();
  const [selectedType, setSelectedType] = useState("bolla_vendita");
  const { documents, error } = useCollection(
    DOC_TYPES[selectedType].collection,
  );
  const { documents: projects } = useCollection("projects");
  const { updateDocument, deleteDocument, response } = useFirestore(
    DOC_TYPES[selectedType].collection,
  );
  const { addDocument: addRicavo, updateDocument: updateRicavo } =
    useFirestore("ricavi");
  const { addDocument: addConsuntivo } = useFirestore("consuntivi");
  const {
    addDocument: addRegistroAcquisti,
    response: responseRegistroAcquisti,
  } = useFirestore("registroAcquisti");
  const { documents: consuntivi } = useCollection("consuntivi");
  const { documents: ricaviBolleVendita } = useCollection("ricavi", [
    ["sourceCollection", "==", "bolle_vendita"],
  ]);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [splitMode, setSplitMode] = useState(false);
  const [splitItems, setSplitItems] = useState([]);
  const [materialiItems, setMaterialiItems] = useState([]);
  const [materialiFieldKey, setMaterialiFieldKey] = useState("");
  const [showFullImage, setShowFullImage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSavingRegistroMateriali, setIsSavingRegistroMateriali] =
    useState(false);
  const [linkedRicavoId, setLinkedRicavoId] = useState("");
  const [isLoadingRicavoLink, setIsLoadingRicavoLink] = useState(false);
  const [isSavingRicavo, setIsSavingRicavo] = useState(false);

  // Stati per Consuntivo
  const [isConsuntivoMode, setIsConsuntivoMode] = useState(false);
  const [consuntivoStep, setConsuntivoStep] = useState(1);
  const [selectedForConsuntivo, setSelectedForConsuntivo] = useState([]);
  const [consuntivoData, setConsuntivoData] = useState({
    materiali: [],
    manodopera: [],
    dirittiChiamata: [],
  });
  const [consuntivoSaveRicavi, setConsuntivoSaveRicavi] = useState(false);
  const [consuntivoNrFattura, setConsuntivoNrFattura] = useState("");
  const [isConfirmingConsuntivo, setIsConfirmingConsuntivo] = useState(false);
  const [consuntivoMeta, setConsuntivoMeta] = useState({
    cliente: "",
    luogo_lavoro: "",
    settore_nome: "",
    settore_codice: "",
    settore_id: "",
    periodo_mese: "",
    periodo_anno: "",
  });
  const [isGeneratingConsuntivo, setIsGeneratingConsuntivo] = useState(false);
  const [isSavingConsuntivoRicavi, setIsSavingConsuntivoRicavi] =
    useState(false);
  const [consuntivoDownloadUrl, setConsuntivoDownloadUrl] = useState("");
  const [consuntivoDownloadName, setConsuntivoDownloadName] = useState("");
  const [consuntivoBlob, setConsuntivoBlob] = useState(null);

  const [filterClienti, setFilterClienti] = useState([]);
  const [filterSettore, setFilterSettore] = useState(null);
  const [onlyNoSettore, setOnlyNoSettore] = useState(false);
  const [onlyMaterialiMissing, setOnlyMaterialiMissing] = useState(false);
  const [onlyManodoperaMissing, setOnlyManodoperaMissing] = useState(false);

  // Filtri Mese/Anno (Condivisi)
  const [filterMonth, setFilterMonth] = useState(() =>
    String(new Date().getMonth() + 1).padStart(2, "0"),
  );
  const [filterYear, setFilterYear] = useState(() =>
    String(new Date().getFullYear()),
  );
  const [sortConfig, setSortConfig] = useState({
    key: "data_documento",
    dir: "desc",
  });

  useEffect(() => {
    if (selectedType === "generico" && onlyMaterialiMissing) {
      setOnlyMaterialiMissing(false);
    }
  }, [selectedType, onlyMaterialiMissing]);

  useEffect(() => {
    if (selectedType !== "bolla_vendita" && onlyManodoperaMissing) {
      setOnlyManodoperaMissing(false);
    }
  }, [selectedType, onlyManodoperaMissing]);

  const getDocDateParts = (doc) => {
    let dateStr = doc.data_documento || doc.data;
    // Se è un timestamp firestore
    if (doc.createdAt && !dateStr) {
      // Fallback su createdAt se manca la data documento?
      // Meglio di no, usiamo solo i campi espliciti se presenti, altrimenti null
    }

    if (!dateStr) return { month: null, year: null };

    // Try YYYY-MM-DD (input type="date")
    if (typeof dateStr === "string" && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = dateStr.split("-");
      return { year: parts[0], month: parts[1] };
    }
    // Try DD/MM/YYYY (text input)
    if (typeof dateStr === "string" && dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const parts = dateStr.split("/");
      return { year: parts[2], month: parts[1] };
    }
    return { month: null, year: null };
  };

  const getDocMeseCompetenza = (doc) => {
    const { year, month } = getDocDateParts(doc || {});
    if (year && month) return `${year}-${month}`;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  // Periodo (mese/anno) proposto per l'intestazione del consuntivo:
  // ricavato dal documento più datato tra quelli selezionati.
  const getPeriodoDaDocumenti = (docs) => {
    const parts = (docs || [])
      .map((doc) => getDocDateParts(doc))
      .filter((p) => p.year && p.month)
      .sort((a, b) =>
        `${a.year}-${a.month}`.localeCompare(`${b.year}-${b.month}`),
      );

    if (parts.length > 0) {
      return { periodo_mese: parts[0].month, periodo_anno: parts[0].year };
    }

    const d = new Date();
    return {
      periodo_mese: String(d.getMonth() + 1).padStart(2, "0"),
      periodo_anno: String(d.getFullYear()),
    };
  };

  const getDocManodoperaDefaults = (doc) => {
    const breakdown = getDocManodoperaBreakdown(doc);
    const oreOrd =
      breakdown &&
      Number.isFinite(breakdown.oreOrdinarie) &&
      breakdown.oreOrdinarie > 0
        ? String(round2(breakdown.oreOrdinarie).toFixed(2))
        : "";
    const oreStra =
      breakdown &&
      Number.isFinite(breakdown.oreStraordinarie) &&
      breakdown.oreStraordinarie > 0
        ? String(round2(breakdown.oreStraordinarie).toFixed(2))
        : "";
    return {
      oreOrdinarie: oreOrd,
      oreStraordinarie: oreStra,
      mode: breakdown?.mode || "",
    };
  };

  const getDocDirittoChiamataDefault = (doc) => {
    if (!doc) return "";
    const direct = doc.diritto_chiamata ?? doc.dirittoChiamata;
    if (direct != null && direct !== "") return String(direct);
    const nested =
      doc.diritto_chiamata?.valore ??
      doc.dirittoChiamata?.valore ??
      doc.diritto_chiamata_valore;
    if (nested != null && nested !== "") return String(nested);
    return "";
  };

  const usedDocumentIds = useMemo(() => {
    if (!consuntivi) return new Set();
    const ids = new Set();
    consuntivi.forEach((c) => {
      if (c.documenti_inclusi && Array.isArray(c.documenti_inclusi)) {
        c.documenti_inclusi.forEach((id) => ids.add(id));
      }
    });
    return ids;
  }, [consuntivi]);

  const ricavoIdByBollaVenditaId = useMemo(() => {
    const map = new Map();
    (ricaviBolleVendita || []).forEach((r) => {
      const docId = (r?.sourceDocId ?? "").toString();
      if (!docId) return;
      map.set(docId, r?.id || "");
    });
    return map;
  }, [ricaviBolleVendita]);

  const filteredConsuntivoDocuments = useMemo(() => {
    if (!documents) return [];
    return documents.filter((doc) => {
      if (filterClienti && filterClienti.length > 0) {
        const allowed = new Set(
          filterClienti.map((c) => String(c.value).toLowerCase()),
        );
        const current = doc.cliente ? String(doc.cliente).toLowerCase() : "";
        if (!current || !allowed.has(current)) return false;
      }

      if (onlyNoSettore) {
        if (doc.settore) return false;
      } else if (filterSettore && filterSettore.value) {
        if (!doc.settore) return false;
        if (doc.settore !== filterSettore.value) return false;
      }

      if (onlyMaterialiMissing) {
        if (!hasMaterialiNonValorizzati(doc)) return false;
      }

      if (onlyManodoperaMissing) {
        if (!hasManodoperaNonValorizzata(doc)) return false;
      }

      // Filtro Mese/Anno
      if (filterMonth || filterYear) {
        const { month, year } = getDocDateParts(doc);
        if (filterMonth && month !== filterMonth) return false;
        if (filterYear && year !== filterYear) return false;
      }

      return true;
    });
  }, [
    documents,
    filterClienti,
    filterSettore,
    onlyNoSettore,
    onlyMaterialiMissing,
    onlyManodoperaMissing,
    filterMonth,
    filterYear,
  ]);

  const uniqueClients = useMemo(() => {
    if (!documents) return [];
    const clients = new Set(
      documents.map((d) => d.cliente).filter((c) => c && c.trim() !== ""),
    );
    return Array.from(clients).sort();
  }, [documents]);

  const clientOptions = useMemo(() => {
    return uniqueClients.map((c) => ({ value: c, label: c }));
  }, [uniqueClients]);

  const sectorFilterOptions = useMemo(() => {
    if (!documents || !projects) return [];
    const map = new Map();
    documents.forEach((d) => {
      if (!d.settore) return;
      const p = projects.find((proj) => proj.id === d.settore);
      const label = p ? p.nome || p.name || p.id : d.settore;
      map.set(d.settore, label);
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) =>
        String(a.label).localeCompare(String(b.label), "it", {
          sensitivity: "base",
        }),
      );
  }, [documents, projects]);

  // Filtra i documenti in base al termine di ricerca
  const filteredDocuments = useMemo(() => {
    return documents
      ? documents.filter((doc) => {
          if (filterClienti && filterClienti.length > 0) {
            const allowed = new Set(
              filterClienti.map((c) => String(c.value).toLowerCase()),
            );
            const current = doc.cliente
              ? String(doc.cliente).toLowerCase()
              : "";
            if (!current || !allowed.has(current)) return false;
          }

          if (onlyNoSettore && doc.settore) return false;

          if (onlyMaterialiMissing) {
            if (selectedType === "generico") return false;
            if (!hasMaterialiNonValorizzati(doc)) return false;
          }

          if (onlyManodoperaMissing) {
            if (selectedType !== "bolla_vendita") return false;
            if (!hasManodoperaNonValorizzata(doc)) return false;
          }

          // Filtro Mese/Anno
          if (filterMonth || filterYear) {
            const { month, year } = getDocDateParts(doc);
            if (filterMonth && month !== filterMonth) return false;
            if (filterYear && year !== filterYear) return false;
          }

          if (!searchTerm) return true;
          const lowerTerm = searchTerm.toLowerCase();

          // Funzione ricorsiva per cercare in tutti i campi
          const checkValue = (val) => {
            if (val == null) return false;
            if (typeof val === "string")
              return val.toLowerCase().includes(lowerTerm);
            if (typeof val === "number")
              return val.toString().includes(lowerTerm);
            // Gestione Timestamp Firestore
            if (val && typeof val.toDate === "function") {
              return format(val.toDate(), "dd/MM/yyyy").includes(lowerTerm);
            }
            if (Array.isArray(val)) {
              return val.some(checkValue);
            }
            if (typeof val === "object") {
              return Object.values(val).some(checkValue);
            }
            return false;
          };

          // Verifica match nei campi diretti del documento
          const directMatch = Object.values(doc).some(checkValue);
          if (directMatch) return true;

          // Verifica match nel nome del progetto (settore)
          if (doc.settore && projects) {
            const project = projects.find((p) => p.id === doc.settore);
            if (project) {
              const projectName = project.nome || project.name || "";
              if (projectName.toLowerCase().includes(lowerTerm)) return true;
            }
          }

          // Verifica match negli item splittati (incluso il nome del settore/progetto di ogni item)
          if (doc.splitItems && Array.isArray(doc.splitItems)) {
            const splitMatch = doc.splitItems.some((item) => {
              // Cerca nei campi dell'item (descrizione, importo, ecc.)
              if (Object.values(item).some(checkValue)) return true;

              // Cerca nel nome del settore dell'item
              if (item.settore && projects) {
                const itemProject = projects.find((p) => p.id === item.settore);
                if (itemProject) {
                  const itemProjectName =
                    itemProject.nome || itemProject.name || "";
                  if (itemProjectName.toLowerCase().includes(lowerTerm))
                    return true;
                }
              }
              return false;
            });
            if (splitMatch) return true;
          }

          return false;
        })
      : [];
  }, [
    documents,
    filterClienti,
    onlyNoSettore,
    onlyMaterialiMissing,
    onlyManodoperaMissing,
    selectedType,
    filterMonth,
    filterYear,
    searchTerm,
    projects,
  ]);

  const sortedDocuments = useMemo(() => {
    if (!filteredDocuments) return [];

    const sorted = [...filteredDocuments];
    const { key, dir } = sortConfig;

    sorted.sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Helper to get comparable value
      const getComparable = (val, k) => {
        if (!val) return "";

        // Date handling
        if (k.includes("data") || k === "createdAt") {
          if (val && typeof val.toDate === "function") {
            return val.toDate().getTime();
          }
          if (typeof val === "string") {
            // Try YYYY-MM-DD
            if (val.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return new Date(val).getTime();
            }
            // Try DD/MM/YYYY
            if (val.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const parts = val.split("/");
              return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
            }
          }
        }

        if (typeof val === "string") return val.toLowerCase();
        if (typeof val === "number") return val;
        return val;
      };

      const valA = getComparable(aVal, key);
      const valB = getComparable(bVal, key);

      if (valA < valB) return dir === "asc" ? -1 : 1;
      if (valA > valB) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredDocuments, sortConfig]);

  const sortedConsuntivoDocuments = useMemo(() => {
    if (!filteredConsuntivoDocuments) return [];

    const sorted = [...filteredConsuntivoDocuments];
    const { key, dir } = sortConfig;

    sorted.sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Helper to get comparable value
      const getComparable = (val, k) => {
        if (!val) return "";

        // Date handling
        if (k.includes("data") || k === "createdAt") {
          if (val && typeof val.toDate === "function") {
            return val.toDate().getTime();
          }
          if (typeof val === "string") {
            // Try YYYY-MM-DD
            if (val.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return new Date(val).getTime();
            }
            // Try DD/MM/YYYY
            if (val.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const parts = val.split("/");
              return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
            }
          }
        }

        if (typeof val === "string") return val.toLowerCase();
        if (typeof val === "number") return val;
        return val;
      };

      const valA = getComparable(aVal, key);
      const valB = getComparable(bVal, key);

      if (valA < valB) return dir === "asc" ? -1 : 1;
      if (valA > valB) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredConsuntivoDocuments, sortConfig]);

  const handleHeaderSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    if (selectedDoc) {
      const manodoperaDefaults = getDocManodoperaDefaults(selectedDoc);
      const hasStoredBreakdown = hasStoredManodoperaBreakdown(selectedDoc);
      setEditFormData({
        ...selectedDoc,
        totale_manodopera_ore: hasStoredBreakdown
          ? selectedDoc.totale_manodopera_ore != null &&
            selectedDoc.totale_manodopera_ore !== ""
            ? String(selectedDoc.totale_manodopera_ore)
            : ""
          : manodoperaDefaults.oreOrdinarie,
        totale_manodopera_straordinaria_ore: hasStoredBreakdown
          ? selectedDoc.totale_manodopera_straordinaria_ore != null &&
            selectedDoc.totale_manodopera_straordinaria_ore !== ""
            ? String(selectedDoc.totale_manodopera_straordinaria_ore)
            : ""
          : manodoperaDefaults.oreStraordinarie,
        tariffa_manodopera:
          selectedDoc.tariffa_manodopera != null &&
          selectedDoc.tariffa_manodopera !== ""
            ? String(selectedDoc.tariffa_manodopera)
            : "",
        tariffa_manodopera_straordinaria:
          selectedDoc.tariffa_manodopera_straordinaria != null &&
          selectedDoc.tariffa_manodopera_straordinaria !== ""
            ? String(selectedDoc.tariffa_manodopera_straordinaria)
            : "",
        diritto_chiamata:
          selectedDoc.diritto_chiamata != null &&
          selectedDoc.diritto_chiamata !== ""
            ? String(selectedDoc.diritto_chiamata)
            : getDocDirittoChiamataDefault(selectedDoc),
        diritto_chiamata_qta:
          selectedDoc.diritto_chiamata_qta != null &&
          selectedDoc.diritto_chiamata_qta !== ""
            ? String(selectedDoc.diritto_chiamata_qta)
            : "1",
        ricavoId: selectedDoc.ricavoId || "",
      });
      if (selectedDoc.splitItems && selectedDoc.splitItems.length > 0) {
        setSplitMode(true);
        setSplitItems(selectedDoc.splitItems);
      } else {
        setSplitMode(false);
        setSplitItems([]);
      }

      const matFieldKey = Array.isArray(selectedDoc.elenco_articoli)
        ? "elenco_articoli"
        : Array.isArray(selectedDoc.materiale_utilizzato)
          ? "materiale_utilizzato"
          : selectedType === "bolla_acquisto"
            ? "elenco_articoli"
            : selectedType === "bolla_vendita"
              ? "materiale_utilizzato"
              : "";
      setMaterialiFieldKey(matFieldKey);
      setMaterialiItems(
        matFieldKey
          ? (selectedDoc[matFieldKey] || []).map((m, index) => ({
              ...m,
              __rowId: m?.id || `${selectedDoc.id || "doc"}-${index}`,
              codice:
                m?.codice ||
                m?.codice_materiale ||
                m?.codiceMateriale ||
                m?.sku ||
                "",
              materialeSalvato: Boolean(m?.materialeSalvato),
              descrizione: m?.descrizione || m?.nome || "",
              quantita:
                m?.quantita != null && m?.quantita !== ""
                  ? `${m.quantita}`
                  : "",
              unita_misura: m?.unita_misura || m?.unita || m?.um || "",
              prezzo:
                m?.prezzo != null && m?.prezzo !== ""
                  ? `${m.prezzo}`
                  : m?.prezzo_unitario != null && m?.prezzo_unitario !== ""
                    ? `${m.prezzo_unitario}`
                    : "",
              ricarico:
                m?.ricarico != null && m?.ricarico !== ""
                  ? `${m.ricarico}`
                  : m?.prezzo_unitario != null && m?.prezzo_unitario !== ""
                    ? "0"
                    : "",
              prezzo_unitario:
                m?.prezzo_unitario != null && m?.prezzo_unitario !== ""
                  ? `${m.prezzo_unitario}`
                  : "",
            }))
          : [],
      );
    }
  }, [selectedDoc, selectedType]);

  useEffect(() => {
    if (!selectedDoc || selectedType !== "bolla_vendita") {
      setLinkedRicavoId("");
      setIsLoadingRicavoLink(false);
      return;
    }

    const foundId = ricavoIdByBollaVenditaId.get(selectedDoc.id) || "";
    setLinkedRicavoId(foundId);
    setIsLoadingRicavoLink(false);

    const staleDocRicavoId = Boolean(selectedDoc?.ricavoId) && !foundId;
    if (staleDocRicavoId) {
      setEditFormData((prev) => ({ ...prev, ricavoId: "" }));
      setSelectedDoc((prev) => (prev ? { ...prev, ricavoId: "" } : prev));
      updateDocument(selectedDoc.id, { ricavoId: "" });
    }
  }, [selectedDoc, selectedType, ricavoIdByBollaVenditaId, updateDocument]);

  const handleCardClick = (doc) => {
    setSelectedDoc(doc);
  };

  const handleCloseModal = () => {
    setSelectedDoc(null);
    setEditFormData({});
    setSplitMode(false);
    setSplitItems([]);
    setMaterialiItems([]);
    setMaterialiFieldKey("");
    setShowFullImage(false);
    setLinkedRicavoId("");
    setIsLoadingRicavoLink(false);
    setIsSavingRicavo(false);
  };

  const parseNumberIT = (value) => {
    if (value == null) return NaN;
    const raw = value.toString().trim();
    if (raw === "") return NaN;

    let s = raw.replace(/€/g, "").replace(/\s+/g, "");

    if (s.includes(",")) {
      s = s.replace(/\./g, "");
      s = s.replace(/,/g, ".");
    }

    s = s.replace(/[^0-9.-]/g, "");
    const n = parseFloat(s);
    return isNaN(n) ? NaN : n;
  };

  const toMaybeNumber = (value) => {
    const n = parseNumberIT(value);
    return isNaN(n) ? "" : n;
  };

  const getMaterialePrezzoUnitario = (materiale) => {
    const prezzo = parseNumberIT(materiale?.prezzo);
    const ricarico = parseNumberIT(materiale?.ricarico);

    if (!isNaN(prezzo)) {
      const perc = isNaN(ricarico) ? 0 : ricarico;
      const unit = prezzo * (1 + perc / 100);
      return isNaN(unit) ? NaN : unit;
    }

    const unitFallback = parseNumberIT(materiale?.prezzo_unitario);
    return isNaN(unitFallback) ? NaN : unitFallback;
  };

  const getMaterialeTotal = (materiale) => {
    const q = parseNumberIT(materiale?.quantita);
    const pu = getMaterialePrezzoUnitario(materiale);
    if (isNaN(q) || isNaN(pu)) return { value: "", text: "" };
    const total = q * pu;
    if (isNaN(total)) return { value: "", text: "" };
    return { value: total, text: total.toFixed(2) };
  };

  const buildCurrentDocPayload = () => {
    if (!selectedDoc) return null;
    const dataToSave = { ...editFormData };

    if (selectedType === "bolla_vendita") {
      dataToSave.manodopera_breakdown_inizializzato = true;
    }

    if (materialiFieldKey) {
      dataToSave[materialiFieldKey] = materialiItems.map((m) => {
        const quantitaValue = toMaybeNumber(m.quantita);
        const prezzoValue = toMaybeNumber(m.prezzo);
        const ricaricoValue = toMaybeNumber(m.ricarico);

        let prezzoUnitarioFinal = "";
        if (prezzoValue !== "") {
          const perc = ricaricoValue === "" ? 0 : ricaricoValue;
          const unit = prezzoValue * (1 + perc / 100);
          prezzoUnitarioFinal = isNaN(unit) ? "" : Math.round(unit * 100) / 100;
        } else {
          prezzoUnitarioFinal = toMaybeNumber(m.prezzo_unitario);
        }

        return {
          codice: m.codice || "",
          materialeSalvato: Boolean(m.materialeSalvato),
          descrizione: m.descrizione || "",
          quantita: quantitaValue === "" ? m.quantita || "" : quantitaValue,
          unita_misura: m.unita_misura || "",
          prezzo: prezzoValue === "" ? m.prezzo || "" : prezzoValue,
          ricarico: ricaricoValue === "" ? m.ricarico || "" : ricaricoValue,
          prezzo_unitario:
            prezzoUnitarioFinal === ""
              ? m.prezzo_unitario || ""
              : prezzoUnitarioFinal,
        };
      });
    }

    if (selectedType === "bolla_acquisto" && splitMode) {
      const rawTotal = Number(dataToSave.importo_totale || 0);
      const docTotal = dataToSave.scorporaIva ? rawTotal / 1.22 : rawTotal;

      const itemsWithPercentage = splitItems.map((item) => ({
        ...item,
        percentuale:
          docTotal > 0
            ? ((Number(item.importo) / docTotal) * 100).toFixed(2)
            : 0,
      }));

      dataToSave.splitItems = itemsWithPercentage;
      dataToSave.isSplit = true;
    } else {
      dataToSave.splitItems = [];
      dataToSave.isSplit = false;
    }

    return dataToSave;
  };

  const addMaterialeRow = () => {
    setMaterialiItems((prev) => [
      ...prev,
      {
        __rowId: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        codice: "",
        materialeSalvato: false,
        descrizione: "",
        quantita: "",
        unita_misura: "",
        prezzo: "",
        ricarico: "",
        prezzo_unitario: "",
      },
    ]);
  };

  const removeMaterialeRow = (rowId) => {
    setMaterialiItems((prev) => prev.filter((m) => m.__rowId !== rowId));
  };

  const updateMaterialeItem = (rowId, field, value) => {
    setMaterialiItems((prev) =>
      prev.map((m) => {
        if (m.__rowId !== rowId) return m;
        const next = { ...m, [field]: value };

        if (field === "prezzo" || field === "ricarico") {
          const prezzo = parseNumberIT(next.prezzo);
          if (isNaN(prezzo)) {
            next.prezzo_unitario = "";
          } else {
            const ricarico = parseNumberIT(next.ricarico);
            const perc = isNaN(ricarico) ? 0 : ricarico;
            const unit = prezzo * (1 + perc / 100);
            next.prezzo_unitario = isNaN(unit) ? "" : unit.toFixed(2);
          }
        }

        return next;
      }),
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Gestione Split Items
  const addSplitItem = () => {
    setSplitItems([
      ...splitItems,
      { id: Date.now(), descrizione: "", importo: 0, settore: "" },
    ]);
  };

  const removeSplitItem = (id) => {
    setSplitItems(splitItems.filter((item) => item.id !== id));
  };

  const updateSplitItem = (id, field, value) => {
    setSplitItems(
      splitItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const calculateTotalSplit = () => {
    return splitItems.reduce((acc, item) => acc + Number(item.importo || 0), 0);
  };

  const handleSplitModeChange = (enable) => {
    setSplitMode(enable);
    if (enable && splitItems.length === 0) {
      if (
        editFormData.elenco_articoli &&
        Array.isArray(editFormData.elenco_articoli)
      ) {
        const mappedItems = editFormData.elenco_articoli.map((item, index) => {
          let amount = 0;
          const parseVal = (val) => {
            if (!val) return 0;
            if (typeof val === "number") return val;
            return parseFloat(
              val.toString().replace("€", "").replace(",", ".").trim(),
            );
          };

          if (item.prezzo_totale || item.importo) {
            amount = parseVal(item.prezzo_totale || item.importo);
          } else if (item.quantita && item.prezzo_unitario) {
            const q = parseVal(item.quantita);
            const p = parseVal(item.prezzo_unitario);
            if (!isNaN(q) && !isNaN(p)) {
              amount = q * p;
            } else if (!isNaN(p)) {
              amount = p;
            }
          } else if (item.prezzo_unitario) {
            amount = parseVal(item.prezzo_unitario);
          }

          return {
            id: Date.now() + index,
            descrizione: item.descrizione || `Articolo ${index + 1}`,
            importo: isNaN(amount) ? 0 : amount.toFixed(2),
            settore: "",
          };
        });

        if (mappedItems.length > 0) {
          setSplitItems(mappedItems);
        }
      }
    }
  };

  const handleSave = async () => {
    if (!selectedDoc) return;
    const dataToSave = buildCurrentDocPayload();
    if (!dataToSave) return;
    await updateDocument(selectedDoc.id, dataToSave);
    if (!response.error) {
      handleCloseModal();
    }
  };

  const handleSaveDocumentoRicavo = async () => {
    if (!selectedDoc) return;
    if (selectedType !== "bolla_vendita") return;
    if (isSavingRicavo) return;

    setIsSavingRicavo(true);
    try {
      const dataToSave = buildCurrentDocPayload();
      if (!dataToSave) return;

      const updated = await updateDocument(selectedDoc.id, dataToSave);
      if (updated === null) {
        alert("Impossibile salvare il documento.");
        return;
      }

      const totMat = (materialiItems || []).reduce((acc, m) => {
        const q = parseNumberIT(m?.quantita);
        const pu = getMaterialePrezzoUnitario(m);
        if (isNaN(q) || isNaN(pu)) return acc;
        return acc + q * pu;
      }, 0);

      const oreOrdParsed = parseNumberIT(dataToSave.totale_manodopera_ore);
      const oreManodoperaOrd = isNaN(oreOrdParsed) ? null : oreOrdParsed;
      const tariffaOrdParsed = parseNumberIT(dataToSave.tariffa_manodopera);
      const tariffaManodoperaOrd = isNaN(tariffaOrdParsed)
        ? 0
        : tariffaOrdParsed;
      const importoManodoperaOrd =
        oreManodoperaOrd == null
          ? 0
          : Math.round(oreManodoperaOrd * tariffaManodoperaOrd * 100) / 100;

      const oreStraParsed = parseNumberIT(
        dataToSave.totale_manodopera_straordinaria_ore,
      );
      const oreManodoperaStra = isNaN(oreStraParsed) ? null : oreStraParsed;
      const tariffaStraParsed = parseNumberIT(
        dataToSave.tariffa_manodopera_straordinaria,
      );
      const tariffaManodoperaStra = isNaN(tariffaStraParsed)
        ? 0
        : tariffaStraParsed;
      const importoManodoperaStra =
        oreManodoperaStra == null
          ? 0
          : Math.round(oreManodoperaStra * tariffaManodoperaStra * 100) / 100;

      const importoManodopera =
        Math.round((importoManodoperaOrd + importoManodoperaStra) * 100) / 100;
      const callFeeQtyParsed = parseNumberIT(dataToSave.diritto_chiamata_qta);
      const dirittoChiamataQta = isNaN(callFeeQtyParsed) ? 1 : callFeeQtyParsed;
      const callFeeUnitParsed = parseNumberIT(dataToSave.diritto_chiamata);
      const dirittoChiamataUnitario = isNaN(callFeeUnitParsed)
        ? 0
        : callFeeUnitParsed;
      const dirittoChiamataTotale =
        Math.round(dirittoChiamataQta * dirittoChiamataUnitario * 100) / 100;
      const importo =
        Math.round((totMat + dirittoChiamataTotale + importoManodopera) * 100) /
        100;

      const cliente = String(dataToSave.cliente || "").trim();
      const numeroDocumento = String(dataToSave.numero_documento || "").trim();
      const luogoIntervento = String(dataToSave.luogo_intervento || "").trim();
      const descrizione = String(dataToSave.oggetto_intervento || "").trim();
      const meseCompetenza = getDocMeseCompetenza(dataToSave);
      const settore = getSettoreCodiceFromProjectId(dataToSave.settore || "");

      const payload = {
        cliente,
        nrFattura: "",
        meseCompetenza,
        settore,
        descrizione: descrizione || "Intervento",
        unitaMisura: "nr",
        importo,
        numero_documento: numeroDocumento,
        luogoIntervento,
        sourceCollection: DOC_TYPES[selectedType].collection,
        sourceDocId: selectedDoc.id,
        totale_materiali: Math.round(totMat * 100) / 100,
        totale_manodopera_ore: oreManodoperaOrd,
        tariffa_manodopera: tariffaManodoperaOrd,
        totale_manodopera_straordinaria_ore: oreManodoperaStra,
        tariffa_manodopera_straordinaria: tariffaManodoperaStra,
        totale_manodopera_straordinaria_importo: importoManodoperaStra,
        totale_manodopera_importo: importoManodopera,
        diritto_chiamata_qta: dirittoChiamataQta,
        diritto_chiamata_prezzo_unitario: dirittoChiamataUnitario,
        totale_diritto_chiamata: dirittoChiamataTotale,
        diritto_chiamata: dirittoChiamataTotale,
      };

      const existingId = linkedRicavoId || "";
      if (existingId) {
        const ricavoUpdated = await updateRicavo(existingId, {
          ...payload,
          updatedAt: timestamp.fromDate(new Date()),
          updatedBy: user?.uid || "",
          updatedByName: user?.displayName || user?.email || "",
        });
        if (ricavoUpdated === null) {
          alert("Impossibile aggiornare il ricavo.");
          return;
        }
        setLinkedRicavoId(existingId);
        handleCloseModal();
        return;
      }

      const created = await addRicavo({
        ...payload,
        createdBy: user?.uid || "",
        createdByName: user?.displayName || user?.email || "",
      });
      const createdId = created?.id || "";
      if (!createdId) {
        alert("Impossibile creare il ricavo.");
        return;
      }
      setLinkedRicavoId(createdId);
      setEditFormData((prev) => ({ ...prev, ricavoId: createdId }));
      await updateDocument(selectedDoc.id, { ricavoId: createdId });
      handleCloseModal();
    } finally {
      setIsSavingRicavo(false);
    }
  };

  const handleDelete = async (doc = null) => {
    const targetDoc = doc || selectedDoc;
    if (!targetDoc) return;
    if (window.confirm("Sei sicuro di voler eliminare questo documento?")) {
      await deleteDocument(targetDoc.id);
      if (!response.error) {
        handleCloseModal();
      }
    }
  };

  // --- LOGICA CONSUNTIVO ---

  const handleToggleConsuntivo = () => {
    if (isConsuntivoMode) {
      setIsConsuntivoMode(false);
      setConsuntivoStep(1);
      setSelectedForConsuntivo([]);
      setConsuntivoData({ materiali: [], manodopera: [], dirittiChiamata: [] });
      setConsuntivoSaveRicavi(false);
      setConsuntivoNrFattura("");
      setIsConfirmingConsuntivo(false);
    } else {
      setIsConsuntivoMode(true);
      setSelectedType("bolla_vendita"); // Forza visualizzazione bolle vendita
    }
  };

  const handleSelectForConsuntivo = (docId) => {
    setSelectedForConsuntivo((prev) => {
      if (prev.includes(docId)) {
        return prev.filter((id) => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const handleProceedToPricing = () => {
    if (selectedForConsuntivo.length === 0) {
      alert("Seleziona almeno un documento.");
      return;
    }

    const selectedDocs = documents.filter((doc) =>
      selectedForConsuntivo.includes(doc.id),
    );

    // Estrai metadati dal primo documento
    const firstDoc = selectedDocs[0] || {};
    let settoreInfo = { nome: "", codice: "", id: "" };
    if (firstDoc.settore && projects) {
      const p = projects.find((proj) => proj.id === firstDoc.settore);
      if (p) {
        settoreInfo = {
          nome: p.nome || p.name || "",
          codice: p.codice || "",
          id: p.id,
        };
      }
    }

    setConsuntivoMeta({
      cliente: firstDoc.cliente || "",
      luogo_lavoro: firstDoc.luogo_intervento || "",
      settore_nome: settoreInfo.nome,
      settore_codice: settoreInfo.codice,
      settore_id: settoreInfo.id,
      ...getPeriodoDaDocumenti(selectedDocs),
    });

    let allMateriali = [];
    let allManodopera = [];
    let allDirittiChiamata = [];

    selectedDocs.forEach((doc) => {
      const docRef = doc.numero_documento || "N/A";

      const breakdown = getDocManodoperaBreakdown(doc);
      const oreOrd =
        breakdown && Number.isFinite(breakdown.oreOrdinarie)
          ? breakdown.oreOrdinarie
          : 0;
      const oreStra =
        breakdown && Number.isFinite(breakdown.oreStraordinarie)
          ? breakdown.oreStraordinarie
          : 0;

      const tariffaOrdParsed = parseNumberIT(doc?.tariffa_manodopera);
      const tariffaManodoperaOrd = isNaN(tariffaOrdParsed)
        ? 0
        : tariffaOrdParsed;
      const tariffaStraParsed = parseNumberIT(
        doc?.tariffa_manodopera_straordinaria,
      );
      const tariffaManodoperaStra = isNaN(tariffaStraParsed)
        ? 0
        : tariffaStraParsed;

      if (Number.isFinite(oreOrd) && oreOrd > 0) {
        allManodopera.push({
          nome_tecnico: "Manodopera",
          giorno_intervento: doc.data_documento || "",
          totale_ore: round2(oreOrd),
          tariffa_oraria: tariffaManodoperaOrd,
          docRef,
          docId: doc.id,
          id: Math.random().toString(36).substr(2, 9),
        });
      }

      if (Number.isFinite(oreStra) && oreStra > 0) {
        allManodopera.push({
          nome_tecnico: "Manodopera Straordinaria",
          giorno_intervento: doc.data_documento || "",
          totale_ore: round2(oreStra),
          tariffa_oraria: tariffaManodoperaStra,
          docRef,
          docId: doc.id,
          id: Math.random().toString(36).substr(2, 9),
        });
      }

      const qtaParsed = parseNumberIT(doc?.diritto_chiamata_qta);
      const dirittoChiamataQta = isNaN(qtaParsed) ? 1 : qtaParsed;
      const unitParsed = parseNumberIT(doc?.diritto_chiamata);
      const dirittoChiamataUnit = isNaN(unitParsed) ? 0 : unitParsed;
      const callFeeTot = dirittoChiamataQta * dirittoChiamataUnit;
      allDirittiChiamata.push({
        docId: doc.id,
        docRef,
        applicato: Number.isFinite(callFeeTot) && callFeeTot > 0,
        valore: Number.isFinite(callFeeTot) ? callFeeTot : 0,
        qta: dirittoChiamataQta,
        prezzo_unitario: dirittoChiamataUnit,
        id: Math.random().toString(36).substr(2, 9),
      });

      // Estrai Materiali
      if (doc.materiale_utilizzato && Array.isArray(doc.materiale_utilizzato)) {
        doc.materiale_utilizzato.forEach((mat) => {
          allMateriali.push({
            ...mat,
            docRef: doc.numero_documento || "N/A",
            docId: doc.id,
            quantita: mat.quantita || 0,
            prezzo_unitario: mat.prezzo_unitario || 0, // Default 0 se manca
            id: Math.random().toString(36).substr(2, 9),
          });
        });
      }
    });

    setConsuntivoData({
      materiali: allMateriali,
      manodopera: allManodopera,
      dirittiChiamata: allDirittiChiamata,
    });
    setConsuntivoStep(2);
  };

  const handleSaveConsuntivoToCloud = async ({ nrFattura }) => {
    const totaleMateriali = consuntivoData.materiali.reduce((acc, m) => {
      const q = parseNumberIT(m.quantita);
      const pu = parseNumberIT(m.prezzo_unitario);
      return acc + (isNaN(q) || isNaN(pu) ? 0 : q * pu);
    }, 0);
    const totaleManodopera = consuntivoData.manodopera.reduce((acc, m) => {
      const ore = parseNumberIT(m.totale_ore);
      const tariffa = parseNumberIT(m.tariffa_oraria);
      return acc + (isNaN(ore) || isNaN(tariffa) ? 0 : ore * tariffa);
    }, 0);
    const totaleDirittiChiamata = consuntivoData.dirittiChiamata.reduce(
      (acc, d) => {
        if (!d.applicato) return acc;
        const v = parseNumberIT(d.valore);
        return acc + (isNaN(v) ? 0 : v);
      },
      0,
    );
    const totaleComplessivo =
      totaleMateriali + totaleManodopera + totaleDirittiChiamata;

    const docToSave = {
      data_creazione: new Date(),
      autore_id: user ? user.uid : "unknown",
      autore_nome: user ? user.displayName || user.email : "unknown",
      cliente: consuntivoMeta.cliente || "Sconosciuto",
      nrFattura: (nrFattura ?? "").toString().trim(),
      luogo_lavoro: consuntivoMeta.luogo_lavoro,
      periodo_mese: consuntivoMeta.periodo_mese || "",
      periodo_anno: consuntivoMeta.periodo_anno || "",
      settore: {
        nome: consuntivoMeta.settore_nome,
        codice: consuntivoMeta.settore_codice,
        id: consuntivoMeta.settore_id,
      },
      materiali: consuntivoData.materiali,
      manodopera: consuntivoData.manodopera,
      dirittiChiamata: consuntivoData.dirittiChiamata,
      documenti_inclusi: selectedForConsuntivo,
      totale_materiali: totaleMateriali.toFixed(2),
      totale_manodopera: totaleManodopera.toFixed(2),
      totale_diritti_chiamata: totaleDirittiChiamata.toFixed(2),
      totale_complessivo: totaleComplessivo.toFixed(2),
    };

    return await addConsuntivo(docToSave);
  };

  const handleSaveConsuntivoToRicavi = async ({ nrFattura }) => {
    if (!selectedForConsuntivo || selectedForConsuntivo.length === 0) {
      throw new Error("Nessun documento selezionato");
    }
    if (isSavingConsuntivoRicavi) return;

    setIsSavingConsuntivoRicavi(true);
    try {
      const now = timestamp.fromDate(new Date());
      const batch = database.batch();
      const docById = new Map((documents || []).map((d) => [d.id, d]));

      selectedForConsuntivo.forEach((docId) => {
        const doc = docById.get(docId) || {};

        const docMateriali = (consuntivoData.materiali || []).filter(
          (m) => m?.docId === docId,
        );
        const docManodopera = (consuntivoData.manodopera || []).filter(
          (m) => m?.docId === docId,
        );
        const docDiritti = (consuntivoData.dirittiChiamata || []).filter(
          (d) => d?.docId === docId && d?.applicato,
        );

        const totMat = docMateriali.reduce((acc, m) => {
          const q = parseNumberIT(m?.quantita);
          const pu = parseNumberIT(m?.prezzo_unitario);
          if (!Number.isFinite(q) || !Number.isFinite(pu)) return acc;
          return acc + q * pu;
        }, 0);

        const totMan = docManodopera.reduce((acc, m) => {
          const ore = parseNumberIT(m?.totale_ore);
          const tariffa = parseNumberIT(m?.tariffa_oraria);
          if (!Number.isFinite(ore) || !Number.isFinite(tariffa)) return acc;
          return acc + ore * tariffa;
        }, 0);

        const totDir = docDiritti.reduce((acc, d) => {
          const v = parseNumberIT(d?.valore);
          if (!Number.isFinite(v)) return acc;
          return acc + v;
        }, 0);

        const importo = Math.round((totMat + totMan + totDir) * 100) / 100;

        const settore = getSettoreCodiceFromProjectId(
          doc?.settore || consuntivoMeta.settore_id || "",
        );
        const cliente = String(doc?.cliente || consuntivoMeta.cliente || "")
          .toString()
          .trim();
        const luogoIntervento = String(
          doc?.luogo_intervento || consuntivoMeta.luogo_lavoro || "",
        )
          .toString()
          .trim();
        const numeroDocumento = String(doc?.numero_documento || "").trim();
        const descrizione = String(doc?.oggetto_intervento || "")
          .toString()
          .trim();

        const meseCompetenza = getDocMeseCompetenza(doc);

        const manOrd = (docManodopera || []).find(
          (m) => m?.nome_tecnico === "Manodopera",
        );
        const manStra = (docManodopera || []).find(
          (m) => m?.nome_tecnico === "Manodopera Straordinaria",
        );

        const oreOrdRaw = parseNumberIT(manOrd?.totale_ore);
        const oreManodoperaOrd = Number.isFinite(oreOrdRaw) ? oreOrdRaw : null;
        const tariffaOrdRaw = parseNumberIT(manOrd?.tariffa_oraria);
        const tariffaManodoperaOrd = Number.isFinite(tariffaOrdRaw)
          ? tariffaOrdRaw
          : 0;

        const oreStraRaw = parseNumberIT(manStra?.totale_ore);
        const oreManodoperaStra = Number.isFinite(oreStraRaw)
          ? oreStraRaw
          : null;
        const tariffaStraRaw = parseNumberIT(manStra?.tariffa_oraria);
        const tariffaManodoperaStra = Number.isFinite(tariffaStraRaw)
          ? tariffaStraRaw
          : 0;

        const importoManodoperaStra =
          oreManodoperaStra == null
            ? 0
            : Math.round(oreManodoperaStra * tariffaManodoperaStra * 100) / 100;

        const dirittoQtaRaw = parseNumberIT(doc?.diritto_chiamata_qta);
        const dirittoChiamataQta = Number.isFinite(dirittoQtaRaw)
          ? dirittoQtaRaw
          : 1;
        const dirittoUnitRaw = parseNumberIT(doc?.diritto_chiamata);
        const dirittoChiamataPrezzoUnit = Number.isFinite(dirittoUnitRaw)
          ? dirittoUnitRaw
          : 0;
        const existingId = ricavoIdByBollaVenditaId.get(docId) || "";
        const ricavoRef = existingId
          ? database.collection("ricavi").doc(existingId)
          : database.collection("ricavi").doc();

        const payload = {
          cliente,
          nrFattura: (nrFattura ?? "").toString().trim(),
          meseCompetenza,
          settore,
          descrizione: descrizione || "Intervento",
          unitaMisura: "nr",
          importo,
          numero_documento: numeroDocumento,
          luogoIntervento,
          sourceCollection: "bolle_vendita",
          sourceDocId: docId,
          totale_materiali: Math.round(totMat * 100) / 100,
          totale_manodopera_ore: oreManodoperaOrd,
          tariffa_manodopera: tariffaManodoperaOrd,
          totale_manodopera_straordinaria_ore: oreManodoperaStra,
          tariffa_manodopera_straordinaria: tariffaManodoperaStra,
          totale_manodopera_straordinaria_importo: importoManodoperaStra,
          totale_manodopera_importo: Math.round(totMan * 100) / 100,
          diritto_chiamata_qta: dirittoChiamataQta,
          diritto_chiamata_prezzo_unitario: dirittoChiamataPrezzoUnit,
          totale_diritto_chiamata: Math.round(totDir * 100) / 100,
          diritto_chiamata: Math.round(totDir * 100) / 100,
        };

        if (existingId) {
          batch.set(
            ricavoRef,
            {
              ...payload,
              updatedAt: now,
              updatedBy: user?.uid || "",
              updatedByName: user?.displayName || user?.email || "",
            },
            { merge: true },
          );
        } else {
          batch.set(
            ricavoRef,
            {
              ...payload,
              createdAt: now,
              createdBy: user?.uid || "",
              createdByName: user?.displayName || user?.email || "",
            },
            { merge: true },
          );
        }

        const bollaRef = database.collection("bolle_vendita").doc(docId);
        batch.set(bollaRef, { ricavoId: ricavoRef.id }, { merge: true });
      });

      await batch.commit();
      return true;
    } catch (_) {
      throw new Error("Errore salvataggio ricavi");
    } finally {
      setIsSavingConsuntivoRicavi(false);
    }
  };

  const handleConfirmConsuntivo = async () => {
    if (isConfirmingConsuntivo) return;
    setIsConfirmingConsuntivo(true);
    try {
      const nr = (consuntivoNrFattura ?? "").toString().trim();
      const saved = await handleSaveConsuntivoToCloud({ nrFattura: nr });
      if (!saved) {
        alert("Impossibile salvare il consuntivo.");
        return;
      }

      if (consuntivoSaveRicavi) {
        await handleSaveConsuntivoToRicavi({ nrFattura: nr });
        alert("Consuntivo salvato e ricavi aggiornati.");
      } else {
        alert("Consuntivo salvato.");
      }
    } catch (_) {
      alert("Errore durante il salvataggio del consuntivo.");
    } finally {
      setIsConfirmingConsuntivo(false);
    }
  };

  const handleDownloadConsuntivo = async () => {
    setIsGeneratingConsuntivo(true);
    setConsuntivoDownloadUrl("");
    setConsuntivoDownloadName("");
    setConsuntivoBlob(null);
    await downloadConsuntivoPdf({
      consuntivoData: {
        ...consuntivoData,
        ...consuntivoMeta,
        settore: {
          nome: consuntivoMeta.settore_nome,
          codice: consuntivoMeta.settore_codice,
          id: consuntivoMeta.settore_id,
        },
      },
      documents,
      selectedIds: selectedForConsuntivo,
      onReady: (blob, fileName) => {
        const url = URL.createObjectURL(blob);
        setConsuntivoDownloadUrl(url);
        setConsuntivoDownloadName(fileName || "Consuntivo.pdf");
        setConsuntivoBlob(blob);
      },
    });
    setIsGeneratingConsuntivo(false);
  };

  const handleDownloadPdfToDisk = async () => {
    if (!consuntivoBlob) return;
    const fileName = consuntivoDownloadName || "Consuntivo.pdf";
    try {
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: "PDF",
              accept: { "application/pdf": [".pdf"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(consuntivoBlob);
        await writable.close();
      } else {
        const link = document.createElement("a");
        link.href = consuntivoDownloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (_) {}
  };

  const currentConfig = DOC_TYPES[selectedType];

  const projectOptions = projects
    ? projects.map((proj) => ({
        value: proj.id,
        label: `${proj.nome || proj.name || "Progetto senza nome"}${
          proj.codice ? " - " + proj.codice : ""
        }`,
      }))
    : [];

  const projectById = useMemo(() => {
    const map = new Map();
    (projects || []).forEach((p) => {
      if (p?.id) map.set(p.id, p);
    });
    return map;
  }, [projects]);

  const getSettoreCodiceFromProjectId = (projectId) => {
    if (!projectId) return "";
    const p = projectById.get(projectId);
    return p?.codice || projectId;
  };

  const normalizeDescKey = (value) => {
    return (value ?? "").toString().trim().toLowerCase();
  };

  const getSettoreProjectIdForMateriale = (materiale) => {
    if (selectedType !== "bolla_acquisto") return "";

    const defaultSettoreId =
      editFormData?.settore || selectedDoc?.settore || "";

    if (!splitMode) return defaultSettoreId;

    const map = new Map();
    (splitItems || []).forEach((s) => {
      const k = normalizeDescKey(s?.descrizione);
      if (!k) return;
      const settoreId = s?.settore || "";
      if (!settoreId) return;
      map.set(k, settoreId);
    });

    const materialKey = normalizeDescKey(materiale?.descrizione);
    if (materialKey && map.has(materialKey)) return map.get(materialKey);

    return defaultSettoreId;
  };

  const handleSaveMaterialiToRegistroAcquisti = async () => {
    if (!selectedDoc) return;
    if (selectedType !== "bolla_acquisto") return;
    if (!materialiFieldKey) return;

    setIsSavingRegistroMateriali(true);
    try {
      const fornitore = editFormData?.fornitore || selectedDoc?.fornitore || "";
      const dataBolla =
        editFormData?.data_documento || selectedDoc?.data_documento || "";
      const codiceBolla =
        editFormData?.numero_documento || selectedDoc?.numero_documento || "";
      const linkBolla = selectedDoc?.fotoUrl || editFormData?.fotoUrl || "";

      const savedRowIds = new Set();

      for (const m of materialiItems || []) {
        const settoreId = getSettoreProjectIdForMateriale(m);
        const settoreCodice = getSettoreCodiceFromProjectId(settoreId);

        const prezzoParsed = parseNumberIT(m?.prezzo);
        const payload = {
          codice: settoreCodice,
          codiceArticolo: (m?.codice || "").toString().trim(),
          descrizione: (m?.descrizione || "").toString().trim(),
          unita: (m?.unita_misura || "").toString().trim(),
          prezzo: isNaN(prezzoParsed) ? m?.prezzo || "" : prezzoParsed,
          fornitore,
          dataBolla,
          codiceBolla,
          linkBolla,
        };

        await addRegistroAcquisti(payload);
        if (m?.__rowId) savedRowIds.add(m.__rowId);
      }

      const updatedMateriali = (materialiItems || []).map((m) => {
        const quantitaValue = toMaybeNumber(m.quantita);
        const prezzoValue = toMaybeNumber(m.prezzo);
        const ricaricoValue = toMaybeNumber(m.ricarico);

        let prezzoUnitarioFinal = "";
        if (prezzoValue !== "") {
          const perc = ricaricoValue === "" ? 0 : ricaricoValue;
          const unit = prezzoValue * (1 + perc / 100);
          prezzoUnitarioFinal = isNaN(unit) ? "" : Math.round(unit * 100) / 100;
        } else {
          prezzoUnitarioFinal = toMaybeNumber(m.prezzo_unitario);
        }

        const isSaved =
          savedRowIds.has(m.__rowId) || Boolean(m.materialeSalvato);

        return {
          codice: m.codice || "",
          materialeSalvato: isSaved,
          descrizione: m.descrizione || "",
          quantita: quantitaValue === "" ? m.quantita || "" : quantitaValue,
          unita_misura: m.unita_misura || "",
          prezzo: prezzoValue === "" ? m.prezzo || "" : prezzoValue,
          ricarico: ricaricoValue === "" ? m.ricarico || "" : ricaricoValue,
          prezzo_unitario:
            prezzoUnitarioFinal === ""
              ? m.prezzo_unitario || ""
              : prezzoUnitarioFinal,
        };
      });

      setMaterialiItems((prev) =>
        prev.map((m) =>
          savedRowIds.has(m.__rowId) ? { ...m, materialeSalvato: true } : m,
        ),
      );
      setEditFormData((prev) => ({
        ...prev,
        [materialiFieldKey]: updatedMateriali,
      }));
      setSelectedDoc((prev) =>
        prev ? { ...prev, [materialiFieldKey]: updatedMateriali } : prev,
      );
      await updateDocument(selectedDoc.id, {
        [materialiFieldKey]: updatedMateriali,
      });
    } finally {
      setIsSavingRegistroMateriali(false);
    }
  };

  const monthYearValue =
    filterYear && filterMonth ? `${filterYear}-${filterMonth}` : "";

  return (
    <div className="gestione-documenti">
      <div className="gestione-header">
        <div>
          <h1>
            {isConsuntivoMode ? "Generatore Consuntivo" : "Gestione Documenti"}
          </h1>
          <p>
            {isConsuntivoMode
              ? "Seleziona le bolle e crea il consuntivo."
              : "Visualizza, modifica e gestisci i documenti scansionati."}
          </p>
        </div>
        <button className="btn-primary" onClick={handleToggleConsuntivo}>
          {isConsuntivoMode ? "Annulla / Torna alla Lista" : "Crea Consuntivo"}
        </button>
      </div>

      {!isConsuntivoMode ? (
        <>
          <div className="gd-filters">
            <div
              className="gd-type-tabs"
              role="tablist"
              aria-label="Tipo documento"
            >
              {Object.entries(DOC_TYPES).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={selectedType === key}
                  className={`gd-tab ${selectedType === key ? "active" : ""}`}
                  onClick={() => setSelectedType(key)}
                >
                  {config.label}
                </button>
              ))}
            </div>

            <div className="gd-filter-row">
              <div className="gd-filter-field">
                <label>Periodo</label>
                <div className="gd-month">
                  <input
                    type="month"
                    value={monthYearValue}
                    onChange={(e) => {
                      const v = e.target.value || "";
                      if (!v) {
                        setFilterMonth("");
                        setFilterYear("");
                        return;
                      }
                      const [y, m] = v.split("-");
                      setFilterYear(y || "");
                      setFilterMonth(m || "");
                    }}
                  />
                  <button
                    type="button"
                    className="gd-clear-btn"
                    onClick={() => {
                      setFilterMonth("");
                      setFilterYear("");
                    }}
                    disabled={!filterMonth && !filterYear}
                    title="Rimuovi filtro periodo"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="gd-filter-field gd-clienti">
                <label>Clienti</label>
                <Select
                  classNamePrefix="react-select"
                  value={filterClienti}
                  onChange={(opts) => setFilterClienti(opts || [])}
                  options={clientOptions}
                  isMulti
                  isSearchable
                  isClearable
                  placeholder="Filtra clienti..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              <div className="gd-filter-field gd-flags">
                <label>Avanzati</label>
                <div className="gd-chips">
                  <button
                    type="button"
                    className={`gd-chip ${onlyNoSettore ? "active" : ""}`}
                    onClick={() => setOnlyNoSettore((p) => !p)}
                  >
                    Senza settore
                  </button>
                  <button
                    type="button"
                    className={`gd-chip ${onlyMaterialiMissing ? "active" : ""}`}
                    onClick={() => setOnlyMaterialiMissing((p) => !p)}
                    disabled={selectedType === "generico"}
                    title={
                      selectedType === "generico"
                        ? "Filtro non disponibile per documenti generici"
                        : ""
                    }
                  >
                    Materiali non valorizzati
                  </button>
                  <button
                    type="button"
                    className={`gd-chip ${
                      onlyManodoperaMissing ? "active" : ""
                    }`}
                    onClick={() => setOnlyManodoperaMissing((p) => !p)}
                    disabled={selectedType !== "bolla_vendita"}
                    title={
                      selectedType !== "bolla_vendita"
                        ? "Filtro disponibile solo per Bolle di Vendita"
                        : ""
                    }
                  >
                    Manodopera non valorizzata
                  </button>
                </div>
              </div>

              <div className="gd-filter-field gd-search">
                <label>Cerca</label>
                <input
                  type="text"
                  placeholder="Cerca in tutti i campi..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>

          {error && <p className="error">{error}</p>}

          <div className="project-table-wrapper">
            {documents && documents.length === 0 && (
              <p className="no-docs" style={{ padding: "20px" }}>
                Nessun documento trovato in questa categoria.
              </p>
            )}
            {documents &&
              documents.length > 0 &&
              filteredDocuments.length === 0 && (
                <p className="no-docs" style={{ padding: "20px" }}>
                  Nessun documento corrisponde ai criteri di ricerca.
                </p>
              )}

            {sortedDocuments && sortedDocuments.length > 0 && (
              <table className="project-table">
                <thead>
                  <tr>
                    {selectedType === "bolla_vendita" && (
                      <th style={{ width: 40 }} title="Ricavo collegato">
                        €
                      </th>
                    )}
                    <th style={{ width: "50px" }}>Tipo</th>

                    {currentConfig.fields.map((field) => (
                      <th
                        key={field.key}
                        className={field.sortable === false ? "" : "sortable"}
                        onClick={() => {
                          if (field.sortable === false) return;
                          handleHeaderSort(field.key);
                        }}
                      >
                        {field.label}{" "}
                        {field.sortable === false
                          ? ""
                          : sortConfig.key === field.key
                            ? sortConfig.dir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                      </th>
                    ))}

                    <th>Settore</th>
                    <th>Inserito il</th>

                    <th style={{ textAlign: "right" }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.map((doc) =>
                    (() => {
                      const ricavoId =
                        ricavoIdByBollaVenditaId.get(doc.id) || "";
                      const hasRicavo = Boolean(ricavoId);
                      return (
                        <tr
                          key={doc.id}
                          className="clickable-row"
                          onClick={() => handleCardClick(doc)}
                        >
                          {selectedType === "bolla_vendita" && (
                            <td
                              title={
                                hasRicavo
                                  ? `Ricavo presente (${ricavoId})`
                                  : "Nessun ricavo"
                              }
                              style={{
                                textAlign: "center",
                                fontWeight: 900,
                                color: hasRicavo ? "#1b5e20" : "#999",
                              }}
                            >
                              {hasRicavo ? "✓" : ""}
                            </td>
                          )}
                          <td>
                            <div className="doc-icon">
                              {doc.mimeType === "application/pdf" ||
                              (doc.fotoUrl && doc.fotoUrl.includes(".pdf")) ? (
                                <span
                                  className="material-icons"
                                  style={{ color: "#e74c3c" }}
                                >
                                  picture_as_pdf
                                </span>
                              ) : doc.fotoUrl ? (
                                <span
                                  className="material-icons"
                                  style={{ color: "#3498db" }}
                                >
                                  image
                                </span>
                              ) : (
                                <span
                                  className="material-icons"
                                  style={{ color: "#95a5a6" }}
                                >
                                  description
                                </span>
                              )}
                            </div>
                          </td>

                          {currentConfig.fields.map((field) => (
                            <td key={field.key}>
                              {(() => {
                                if (field.key === "__materiali_quantificati") {
                                  const items = Array.isArray(
                                    doc.materiale_utilizzato,
                                  )
                                    ? doc.materiale_utilizzato
                                    : Array.isArray(doc.elenco_articoli)
                                      ? doc.elenco_articoli
                                      : [];
                                  const m = items.length;
                                  const n = items.reduce((acc, it) => {
                                    const pu = it?.prezzo_unitario;
                                    if (typeof pu === "number")
                                      return acc + (isNaN(pu) ? 0 : 1);
                                    if (pu == null) return acc;
                                    if (typeof pu === "string")
                                      return acc + (pu.trim() === "" ? 0 : 1);
                                    return acc + 1;
                                  }, 0);
                                  return `${n}/${m}`;
                                }

                                if (field.key === "__manodopera_quantificata") {
                                  const { x, y } = getManodoperaXY(doc);
                                  return `${x}/${y}`;
                                }

                                const val = doc[field.key];
                                if (field.type === "date") {
                                  if (
                                    val &&
                                    typeof val === "string" &&
                                    val.match(/^\d{4}-\d{2}-\d{2}$/)
                                  ) {
                                    const [y, m, d] = val.split("-");
                                    return `${d}/${m}/${y}`;
                                  }
                                  return val;
                                }
                                if (
                                  field.type === "number" ||
                                  field.key.includes("importo")
                                ) {
                                  return val
                                    ? `€ ${Number(val).toFixed(2)}`
                                    : "-";
                                }
                                if (val && typeof val === "object")
                                  return JSON.stringify(val);
                                return val;
                              })()}
                            </td>
                          ))}

                          <td>
                            {doc.isSplit
                              ? "Multiplo"
                              : doc.settore
                                ? projects &&
                                  projects.find((p) => p.id === doc.settore)
                                  ? projects.find((p) => p.id === doc.settore)
                                      .nome ||
                                    projects.find((p) => p.id === doc.settore)
                                      .name ||
                                    "Progetto"
                                  : "Sconosciuto"
                                : "Non assegnato"}
                          </td>

                          <td>
                            {doc.createdAt
                              ? format(doc.createdAt.toDate(), "dd/MM/yyyy")
                              : "N/A"}
                          </td>

                          <td>
                            <div className="actions">
                              {doc.fotoUrl && (
                                <button
                                  className="btn-icon download"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(doc.fotoUrl, "_blank");
                                  }}
                                  title="Visualizza/Scarica"
                                >
                                  <span className="material-icons">
                                    visibility
                                  </span>
                                </button>
                              )}
                              <button
                                className="btn-icon delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(doc);
                                }}
                                title="Elimina"
                              >
                                <span className="material-icons">delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })(),
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="consuntivo-wizard">
          {consuntivoStep === 1 && (
            <div className="step-container">
              <h2>1. Seleziona le Bolle di Vendita</h2>

              <div
                className="gd-filters gd-filters-consuntivo"
                style={{ marginBottom: "20px" }}
              >
                <div className="gd-filter-row">
                  <div className="gd-filter-field">
                    <label>Periodo</label>
                    <div className="gd-month">
                      <input
                        type="month"
                        value={monthYearValue}
                        onChange={(e) => {
                          const v = e.target.value || "";
                          if (!v) {
                            setFilterMonth("");
                            setFilterYear("");
                            return;
                          }
                          const [y, m] = v.split("-");
                          setFilterYear(y || "");
                          setFilterMonth(m || "");
                        }}
                      />
                      <button
                        type="button"
                        className="gd-clear-btn"
                        onClick={() => {
                          setFilterMonth("");
                          setFilterYear("");
                        }}
                        disabled={!filterMonth && !filterYear}
                        title="Rimuovi filtro periodo"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="gd-filter-field gd-clienti">
                    <label>Clienti</label>
                    <Select
                      classNamePrefix="react-select"
                      value={filterClienti}
                      onChange={(opts) => setFilterClienti(opts || [])}
                      options={clientOptions}
                      isMulti
                      isSearchable
                      isClearable
                      placeholder="Filtra clienti..."
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>

                  <div className="gd-filter-field">
                    <label>Settore</label>
                    <Select
                      classNamePrefix="react-select"
                      value={filterSettore}
                      onChange={(opt) => setFilterSettore(opt)}
                      options={sectorFilterOptions}
                      isSearchable
                      isClearable
                      placeholder="Filtra settore..."
                      isDisabled={onlyNoSettore}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>

                  <div className="gd-filter-field gd-flags">
                    <label>Avanzati</label>
                    <div className="gd-chips">
                      <button
                        type="button"
                        className={`gd-chip ${onlyNoSettore ? "active" : ""}`}
                        onClick={() => setOnlyNoSettore((p) => !p)}
                      >
                        Senza settore
                      </button>
                      <button
                        type="button"
                        className={`gd-chip ${
                          onlyMaterialiMissing ? "active" : ""
                        }`}
                        onClick={() => setOnlyMaterialiMissing((p) => !p)}
                      >
                        Materiali non valorizzati
                      </button>
                      <button
                        type="button"
                        className={`gd-chip ${
                          onlyManodoperaMissing ? "active" : ""
                        }`}
                        onClick={() => setOnlyManodoperaMissing((p) => !p)}
                      >
                        Manodopera non valorizzata
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="project-table-wrapper">
                {sortedConsuntivoDocuments &&
                sortedConsuntivoDocuments.length > 0 ? (
                  <table className="project-table">
                    <thead>
                      <tr>
                        <th style={{ width: "50px" }}>
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedForConsuntivo(
                                  sortedConsuntivoDocuments.map((d) => d.id),
                                );
                              } else {
                                setSelectedForConsuntivo([]);
                              }
                            }}
                            checked={
                              sortedConsuntivoDocuments.length > 0 &&
                              selectedForConsuntivo.length ===
                                sortedConsuntivoDocuments.length
                            }
                          />
                        </th>
                        <th
                          className="sortable"
                          onClick={() => handleHeaderSort("numero_documento")}
                        >
                          N. Doc{" "}
                          {sortConfig.key === "numero_documento"
                            ? sortConfig.dir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                        <th
                          className="sortable"
                          onClick={() => handleHeaderSort("data_documento")}
                        >
                          Data{" "}
                          {sortConfig.key === "data_documento"
                            ? sortConfig.dir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                        <th
                          className="sortable"
                          onClick={() => handleHeaderSort("cliente")}
                        >
                          Cliente{" "}
                          {sortConfig.key === "cliente"
                            ? sortConfig.dir === "asc"
                              ? "▲"
                              : "▼"
                            : ""}
                        </th>
                        <th>Luogo</th>
                        <th>Settore</th>
                        <th>Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedConsuntivoDocuments.map((doc) => {
                        const isUsed = usedDocumentIds.has(doc.id);
                        const isSelected = selectedForConsuntivo.includes(
                          doc.id,
                        );
                        return (
                          <tr
                            key={doc.id}
                            className={`clickable-row ${
                              isSelected ? "selected-row" : ""
                            }`}
                            onClick={() => handleSelectForConsuntivo(doc.id)}
                            style={{ opacity: isUsed ? 0.8 : 1 }}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  handleSelectForConsuntivo(doc.id)
                                }
                              />
                            </td>
                            <td>{doc.numero_documento || "-"}</td>
                            <td>
                              {doc.data_documento
                                ? (() => {
                                    const val = doc.data_documento;
                                    if (
                                      typeof val === "string" &&
                                      val.match(/^\d{4}-\d{2}-\d{2}$/)
                                    ) {
                                      const [y, m, d] = val.split("-");
                                      return `${d}/${m}/${y}`;
                                    }
                                    return val;
                                  })()
                                : "-"}
                            </td>
                            <td>{doc.cliente || "-"}</td>
                            <td>{doc.luogo_intervento || "-"}</td>
                            <td>
                              {doc.settore
                                ? projects &&
                                  projects.find((p) => p.id === doc.settore)
                                  ? projects.find((p) => p.id === doc.settore)
                                      .nome ||
                                    projects.find((p) => p.id === doc.settore)
                                      .name ||
                                    "Progetto"
                                  : "Sconosciuto"
                                : "-"}
                            </td>
                            <td>
                              {isUsed && (
                                <span
                                  style={{
                                    backgroundColor: "#e74c3c",
                                    color: "white",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontSize: "0.8em",
                                    fontWeight: "bold",
                                  }}
                                >
                                  GIÀ USATO
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ textAlign: "center", padding: "20px" }}>
                    Nessun documento trovato con i filtri correnti.
                  </p>
                )}
              </div>
              <button
                className="btn-primary"
                style={{ marginTop: "20px" }}
                onClick={handleProceedToPricing}
              >
                Avanti: Anteprima Consuntivo ({selectedForConsuntivo.length})
              </button>
            </div>
          )}

          {consuntivoStep === 2 && (
            <div className="step-container">
              <h2>2. Anteprima Consuntivo</h2>

              <div
                className="consuntivo-meta-form"
                style={{
                  marginBottom: "20px",
                  padding: "15px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                }}
              >
                <h3 style={{ marginTop: 0 }}>Dati Intestazione</h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div className="form-group">
                    <label>Cliente</label>
                    <input
                      type="text"
                      value={consuntivoMeta.cliente}
                      onChange={(e) =>
                        setConsuntivoMeta({
                          ...consuntivoMeta,
                          cliente: e.target.value,
                        })
                      }
                      style={{ width: "100%", padding: "8px" }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Luogo Lavoro</label>
                    <input
                      type="text"
                      value={consuntivoMeta.luogo_lavoro}
                      onChange={(e) =>
                        setConsuntivoMeta({
                          ...consuntivoMeta,
                          luogo_lavoro: e.target.value,
                        })
                      }
                      style={{ width: "100%", padding: "8px" }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Settore</label>
                    <input
                      type="text"
                      value={consuntivoMeta.settore_nome}
                      onChange={(e) =>
                        setConsuntivoMeta({
                          ...consuntivoMeta,
                          settore_nome: e.target.value,
                        })
                      }
                      style={{ width: "100%", padding: "8px" }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Codice Settore</label>
                    <input
                      type="text"
                      value={consuntivoMeta.settore_codice}
                      onChange={(e) =>
                        setConsuntivoMeta({
                          ...consuntivoMeta,
                          settore_codice: e.target.value,
                        })
                      }
                      style={{ width: "100%", padding: "8px" }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Periodo Consuntivo (mese/anno)</label>
                    <input
                      type="month"
                      value={
                        consuntivoMeta.periodo_anno &&
                        consuntivoMeta.periodo_mese
                          ? `${consuntivoMeta.periodo_anno}-${consuntivoMeta.periodo_mese}`
                          : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value || "";
                        const [y, m] = v.split("-");
                        setConsuntivoMeta({
                          ...consuntivoMeta,
                          periodo_anno: y || "",
                          periodo_mese: m || "",
                        });
                      }}
                      style={{ width: "100%", padding: "8px" }}
                    />
                    <small style={{ color: "#666" }}>
                      Modifica il periodo stampato nell'intestazione del PDF. Se
                      lasciato vuoto viene ricalcolato dalle date dei documenti.
                    </small>
                  </div>
                </div>
              </div>

              <h3>Riepilogo righe</h3>
              <table className="consuntivo-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>€</th>
                    <th>Documento</th>
                    <th>Descrizione</th>
                    <th style={{ textAlign: "right" }}>Materiali</th>
                    <th style={{ textAlign: "right" }}>Manodopera</th>
                    <th style={{ textAlign: "right" }}>Diritto</th>
                    <th style={{ textAlign: "right" }}>Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const docById = new Map(
                      (documents || []).map((d) => [d.id, d]),
                    );
                    const rows = (selectedForConsuntivo || []).map((id) => {
                      const doc = docById.get(id) || {};
                      const hasRicavo = Boolean(
                        ricavoIdByBollaVenditaId.get(id) || "",
                      );

                      const totMat = (consuntivoData.materiali || [])
                        .filter((m) => m?.docId === id)
                        .reduce((acc, m) => {
                          const q = parseNumberIT(m?.quantita);
                          const pu = parseNumberIT(m?.prezzo_unitario);
                          if (!Number.isFinite(q) || !Number.isFinite(pu))
                            return acc;
                          return acc + q * pu;
                        }, 0);

                      const totMan = (consuntivoData.manodopera || [])
                        .filter((m) => m?.docId === id)
                        .reduce((acc, m) => {
                          const ore = parseNumberIT(m?.totale_ore);
                          const tariffa = parseNumberIT(m?.tariffa_oraria);
                          if (
                            !Number.isFinite(ore) ||
                            !Number.isFinite(tariffa)
                          )
                            return acc;
                          return acc + ore * tariffa;
                        }, 0);

                      const totDir = (consuntivoData.dirittiChiamata || [])
                        .filter((d) => d?.docId === id && d?.applicato)
                        .reduce((acc, d) => {
                          const v = parseNumberIT(d?.valore);
                          if (!Number.isFinite(v)) return acc;
                          return acc + v;
                        }, 0);

                      const totale =
                        Math.round((totMat + totMan + totDir) * 100) / 100;

                      return {
                        id,
                        numero: (doc?.numero_documento ?? "").toString(),
                        descrizione: (doc?.oggetto_intervento ?? "")
                          .toString()
                          .trim(),
                        totMat,
                        totMan,
                        totDir,
                        totale,
                        hasRicavo,
                      };
                    });

                    const sumMat = rows.reduce((acc, r) => acc + r.totMat, 0);
                    const sumMan = rows.reduce((acc, r) => acc + r.totMan, 0);
                    const sumDir = rows.reduce((acc, r) => acc + r.totDir, 0);
                    const sumTot = rows.reduce((acc, r) => acc + r.totale, 0);

                    return (
                      <>
                        {rows.map((r) => (
                          <tr key={r.id}>
                            <td
                              style={{
                                textAlign: "center",
                                fontWeight: 900,
                                color: r.hasRicavo ? "#1b5e20" : "#999",
                              }}
                              title={
                                r.hasRicavo
                                  ? "Ricavo presente"
                                  : "Nessun ricavo"
                              }
                            >
                              {r.hasRicavo ? "✓" : ""}
                            </td>
                            <td>{r.numero || r.id}</td>
                            <td>{r.descrizione || "Intervento"}</td>
                            <td style={{ textAlign: "right" }}>
                              € {r.totMat.toFixed(2)}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              € {r.totMan.toFixed(2)}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              € {r.totDir.toFixed(2)}
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 700 }}>
                              € {r.totale.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={3} style={{ fontWeight: 800 }}>
                            Totale
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 800 }}>
                            € {sumMat.toFixed(2)}
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 800 }}>
                            € {sumMan.toFixed(2)}
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 800 }}>
                            € {sumDir.toFixed(2)}
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 900 }}>
                            € {sumTot.toFixed(2)}
                          </td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>

              <div className="preview-summary">
                <p>
                  <strong>Totale Materiali:</strong> €{" "}
                  {consuntivoData.materiali
                    .reduce((acc, m) => {
                      const q = parseNumberIT(m.quantita);
                      const pu = parseNumberIT(m.prezzo_unitario);
                      return acc + (isNaN(q) || isNaN(pu) ? 0 : q * pu);
                    }, 0)
                    .toFixed(2)}
                </p>
                <p>
                  <strong>Totale Manodopera:</strong> €{" "}
                  {consuntivoData.manodopera
                    .reduce((acc, m) => {
                      const ore = parseNumberIT(m.totale_ore);
                      const tariffa = parseNumberIT(m.tariffa_oraria);
                      return (
                        acc + (isNaN(ore) || isNaN(tariffa) ? 0 : ore * tariffa)
                      );
                    }, 0)
                    .toFixed(2)}
                </p>
                <p>
                  <strong>Totale Diritto di Chiamata:</strong> €{" "}
                  {consuntivoData.dirittiChiamata
                    .reduce((acc, d) => {
                      if (!d.applicato) return acc;
                      const v = parseNumberIT(d.valore);
                      return acc + (isNaN(v) ? 0 : v);
                    }, 0)
                    .toFixed(2)}
                </p>
                <h3>
                  TOTALE COMPLESSIVO: €{" "}
                  {(
                    consuntivoData.materiali.reduce((acc, m) => {
                      const q = parseNumberIT(m.quantita);
                      const pu = parseNumberIT(m.prezzo_unitario);
                      return acc + (isNaN(q) || isNaN(pu) ? 0 : q * pu);
                    }, 0) +
                    consuntivoData.manodopera.reduce((acc, m) => {
                      const ore = parseNumberIT(m.totale_ore);
                      const tariffa = parseNumberIT(m.tariffa_oraria);
                      return (
                        acc + (isNaN(ore) || isNaN(tariffa) ? 0 : ore * tariffa)
                      );
                    }, 0) +
                    consuntivoData.dirittiChiamata.reduce((acc, d) => {
                      if (!d.applicato) return acc;
                      const v = parseNumberIT(d.valore);
                      return acc + (isNaN(v) ? 0 : v);
                    }, 0)
                  ).toFixed(2)}
                </h3>
              </div>
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  border: "1px solid #e5e5e5",
                  borderRadius: "6px",
                  background: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    flexWrap: "wrap",
                  }}
                >
                  <label style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={consuntivoSaveRicavi}
                      onChange={(e) =>
                        setConsuntivoSaveRicavi(e.target.checked)
                      }
                      style={{ marginRight: "8px" }}
                    />
                    Salva anche su Ricavi
                  </label>
                  <label style={{ display: "flex", alignItems: "center" }}>
                    Nr fattura (opzionale)
                    <input
                      type="number"
                      value={consuntivoNrFattura}
                      onChange={(e) => setConsuntivoNrFattura(e.target.value)}
                      style={{
                        width: "140px",
                        marginLeft: "8px",
                        padding: "6px 8px",
                      }}
                      placeholder="es. 123"
                    />
                  </label>
                </div>
              </div>

              <div className="wizard-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setConsuntivoStep(1)}
                >
                  Indietro
                </button>
                <button
                  className="btn-primary"
                  onClick={handleDownloadConsuntivo}
                  disabled={isGeneratingConsuntivo}
                >
                  {isGeneratingConsuntivo
                    ? "Generazione..."
                    : "Genera PDF Consuntivo + Bolle"}
                </button>
                {consuntivoDownloadUrl && (
                  <button
                    className="btn-secondary"
                    onClick={handleDownloadPdfToDisk}
                    style={{ marginLeft: "10px" }}
                  >
                    Scarica PDF
                  </button>
                )}

                <button
                  className="btn-success"
                  onClick={handleConfirmConsuntivo}
                  disabled={isConfirmingConsuntivo || isSavingConsuntivoRicavi}
                  style={{
                    marginLeft: "10px",
                    backgroundColor: "#27ae60",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "5px",
                    cursor:
                      isConfirmingConsuntivo || isSavingConsuntivoRicavi
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {isConfirmingConsuntivo
                    ? "Salvataggio..."
                    : "Conferma Consuntivo"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Modifica */}
      {selectedDoc && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div
            className="modal-content-documenti gd-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-modal" onClick={handleCloseModal}>
              &times;
            </button>

            <div className="modal-image-section">
              {selectedDoc.fotoUrl ? (
                selectedDoc.mimeType === "application/pdf" ||
                selectedDoc.fotoUrl.includes(".pdf") ? (
                  <embed
                    src={selectedDoc.fotoUrl}
                    width="100%"
                    height="100%"
                    type="application/pdf"
                  />
                ) : (
                  <img
                    src={selectedDoc.fotoUrl}
                    alt="Documento"
                    onClick={() => setShowFullImage(true)}
                    style={{ cursor: "pointer" }}
                    title="Clicca per ingrandire"
                  />
                )
              ) : (
                <p style={{ color: "white" }}>Nessuna immagine disponibile</p>
              )}
            </div>

            <div className="modal-form-section">
              <h2>Dettagli Documento</h2>

              {currentConfig.fields.map((field) => {
                if (
                  selectedType === "bolla_vendita" &&
                  (field.key === "__materiali_quantificati" ||
                    field.key === "__manodopera_quantificata")
                ) {
                  return null;
                }
                return (
                  <div key={field.key} className="form-group">
                    <label>{field.label}</label>
                    {field.type === "textarea" ? (
                      <textarea
                        name={field.key}
                        value={editFormData[field.key] || ""}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <input
                        type={field.type}
                        name={field.key}
                        value={editFormData[field.key] || ""}
                        onChange={handleInputChange}
                      />
                    )}
                  </div>
                );
              })}

              {materialiFieldKey && (
                <div className="gd-materiali-section">
                  <h3>Materiali</h3>
                  <div
                    className={`gd-materiali-header ${
                      selectedType === "bolla_acquisto" ? "is-acquisto" : ""
                    }`}
                  >
                    {selectedType === "bolla_acquisto" && <span>Codice</span>}
                    <span>Descrizione</span>
                    <span>Q.tà</span>
                    <span>U.M.</span>
                    <span>Prezzo</span>
                    {selectedType !== "bolla_acquisto" && <span>Ricarico</span>}
                    <span>Prezzo Unit.</span>
                    <span>Prezzo Tot.</span>
                    <span></span>
                  </div>
                  {materialiItems.map((m) => {
                    const total = getMaterialeTotal(m);
                    const unit = getMaterialePrezzoUnitario(m);
                    return (
                      <div
                        key={m.__rowId}
                        className={`gd-materiali-row ${
                          selectedType === "bolla_acquisto" ? "is-acquisto" : ""
                        }`}
                      >
                        {selectedType === "bolla_acquisto" && (
                          <input
                            type="text"
                            value={m.codice || ""}
                            onChange={(e) =>
                              updateMaterialeItem(
                                m.__rowId,
                                "codice",
                                e.target.value,
                              )
                            }
                          />
                        )}
                        <input
                          type="text"
                          value={m.descrizione || ""}
                          onChange={(e) =>
                            updateMaterialeItem(
                              m.__rowId,
                              "descrizione",
                              e.target.value,
                            )
                          }
                        />
                        <input
                          type="text"
                          value={m.quantita || ""}
                          onChange={(e) =>
                            updateMaterialeItem(
                              m.__rowId,
                              "quantita",
                              e.target.value,
                            )
                          }
                        />
                        <input
                          type="text"
                          value={m.unita_misura || ""}
                          onChange={(e) =>
                            updateMaterialeItem(
                              m.__rowId,
                              "unita_misura",
                              e.target.value,
                            )
                          }
                        />
                        <input
                          type="text"
                          value={m.prezzo || ""}
                          onChange={(e) =>
                            updateMaterialeItem(
                              m.__rowId,
                              "prezzo",
                              e.target.value,
                            )
                          }
                        />
                        {selectedType !== "bolla_acquisto" && (
                          <input
                            type="text"
                            value={m.ricarico || ""}
                            onChange={(e) =>
                              updateMaterialeItem(
                                m.__rowId,
                                "ricarico",
                                e.target.value,
                              )
                            }
                          />
                        )}
                        <div className="gd-materiali-total">
                          {!isNaN(unit) ? `€${unit.toFixed(2)}` : ""}
                        </div>
                        <div className="gd-materiali-total">
                          {total.text !== "" ? `€${total.text}` : ""}
                        </div>
                        {selectedType === "bolla_acquisto" ? (
                          <div className="gd-materiali-row-actions">
                            {m.materialeSalvato ? (
                              <span
                                className="gd-materiale-saved-check"
                                title="Materiale salvato"
                                aria-label="Materiale salvato"
                              >
                                ✓
                              </span>
                            ) : (
                              <span className="gd-materiale-saved-placeholder" />
                            )}
                            <button
                              type="button"
                              className="gd-materiali-remove"
                              onClick={() => removeMaterialeRow(m.__rowId)}
                              aria-label="Rimuovi riga"
                            >
                              &times;
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="gd-materiali-remove"
                            onClick={() => removeMaterialeRow(m.__rowId)}
                            aria-label="Rimuovi riga"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className="gd-materiali-add"
                    onClick={addMaterialeRow}
                  >
                    +
                  </button>
                </div>
              )}

              {selectedType === "bolla_vendita" && (
                <>
                  <div className="gd-materiali-section">
                    <h3>Valorizzazione</h3>
                    <table className="consuntivo-table">
                      <thead>
                        <tr>
                          <th>Descrizione</th>
                          <th>Q.tà</th>
                          <th>Prezzo</th>
                          <th>Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const ore = parseNumberIT(
                            editFormData.totale_manodopera_ore,
                          );
                          const tariffa = parseNumberIT(
                            editFormData.tariffa_manodopera,
                          );
                          const tot =
                            Number.isFinite(ore) && Number.isFinite(tariffa)
                              ? ore * tariffa
                              : NaN;
                          return (
                            <tr>
                              <td>Manodopera</td>
                              <td>
                                <input
                                  type="number"
                                  name="totale_manodopera_ore"
                                  value={
                                    editFormData.totale_manodopera_ore || ""
                                  }
                                  onChange={handleInputChange}
                                  step="0.25"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="tariffa_manodopera"
                                  value={editFormData.tariffa_manodopera || ""}
                                  onChange={handleInputChange}
                                  step="1.00"
                                />
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {isNaN(tot) ? "" : `€ ${tot.toFixed(2)}`}
                              </td>
                            </tr>
                          );
                        })()}

                        {(() => {
                          const ore = parseNumberIT(
                            editFormData.totale_manodopera_straordinaria_ore,
                          );
                          const tariffa = parseNumberIT(
                            editFormData.tariffa_manodopera_straordinaria,
                          );
                          const tot =
                            Number.isFinite(ore) && Number.isFinite(tariffa)
                              ? ore * tariffa
                              : NaN;
                          return (
                            <tr>
                              <td>Manodopera Straordinaria</td>
                              <td>
                                <input
                                  type="number"
                                  name="totale_manodopera_straordinaria_ore"
                                  value={
                                    editFormData.totale_manodopera_straordinaria_ore ||
                                    ""
                                  }
                                  onChange={handleInputChange}
                                  step="0.25"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="tariffa_manodopera_straordinaria"
                                  value={
                                    editFormData.tariffa_manodopera_straordinaria ||
                                    ""
                                  }
                                  onChange={handleInputChange}
                                  step="1.00"
                                />
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {isNaN(tot) ? "" : `€ ${tot.toFixed(2)}`}
                              </td>
                            </tr>
                          );
                        })()}

                        {(() => {
                          const qta = parseNumberIT(
                            editFormData.diritto_chiamata_qta,
                          );
                          const prezzoUnit = parseNumberIT(
                            editFormData.diritto_chiamata,
                          );
                          const tot =
                            Number.isFinite(qta) && Number.isFinite(prezzoUnit)
                              ? qta * prezzoUnit
                              : NaN;
                          return (
                            <tr>
                              <td>Diritto di Chiamata</td>
                              <td>
                                <input
                                  type="number"
                                  name="diritto_chiamata_qta"
                                  value={
                                    editFormData.diritto_chiamata_qta || ""
                                  }
                                  onChange={handleInputChange}
                                  step="1"
                                  min="0"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="diritto_chiamata"
                                  value={editFormData.diritto_chiamata || ""}
                                  onChange={handleInputChange}
                                  step="0.01"
                                />
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {isNaN(tot) ? "" : `€ ${tot.toFixed(2)}`}
                              </td>
                            </tr>
                          );
                        })()}

                        {(() => {
                          const totMateriali = (materialiItems || []).reduce(
                            (acc, m) => {
                              const q = parseNumberIT(m?.quantita);
                              const pu = getMaterialePrezzoUnitario(m);
                              if (isNaN(q) || isNaN(pu)) return acc;
                              return acc + q * pu;
                            },
                            0,
                          );

                          const ore = parseNumberIT(
                            editFormData.totale_manodopera_ore,
                          );
                          const tariffa = parseNumberIT(
                            editFormData.tariffa_manodopera,
                          );
                          const totManodopera =
                            Number.isFinite(ore) && Number.isFinite(tariffa)
                              ? ore * tariffa
                              : 0;

                          const oreStra = parseNumberIT(
                            editFormData.totale_manodopera_straordinaria_ore,
                          );
                          const tariffaStra = parseNumberIT(
                            editFormData.tariffa_manodopera_straordinaria,
                          );
                          const totManodoperaStra =
                            Number.isFinite(oreStra) &&
                            Number.isFinite(tariffaStra)
                              ? oreStra * tariffaStra
                              : 0;

                          const qta = parseNumberIT(
                            editFormData.diritto_chiamata_qta,
                          );
                          const prezzoUnit = parseNumberIT(
                            editFormData.diritto_chiamata,
                          );
                          const totDiritto =
                            Number.isFinite(qta) && Number.isFinite(prezzoUnit)
                              ? qta * prezzoUnit
                              : 0;

                          const totale =
                            Math.round(
                              (totMateriali +
                                totManodopera +
                                totManodoperaStra +
                                totDiritto) *
                                100,
                            ) / 100;

                          return (
                            <tr>
                              <td colSpan={3} style={{ fontWeight: 700 }}>
                                Totale valorizzazione
                              </td>
                              <td
                                style={{ textAlign: "right", fontWeight: 700 }}
                              >
                                € {totale.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>

                  <div className="form-group">
                    <label>Ricavo collegato</label>
                    <input
                      type="text"
                      value={
                        isLoadingRicavoLink
                          ? "Caricamento..."
                          : linkedRicavoId
                            ? `Si (${linkedRicavoId})`
                            : "No"
                      }
                      readOnly
                    />
                  </div>
                </>
              )}

              {/* Campi extra o JSON raw se necessario */}
              {selectedType === "bolla_acquisto" ? (
                <div className="split-section">
                  <h3>Assegnazione Settore</h3>
                  <div className="split-toggle">
                    <label>
                      <input
                        type="radio"
                        checked={!splitMode}
                        onChange={() => handleSplitModeChange(false)}
                      />
                      Assegnazione Unica
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={splitMode}
                        onChange={() => handleSplitModeChange(true)}
                      />
                      Spaccatura per Materiale
                    </label>
                  </div>

                  {!splitMode ? (
                    <div className="form-group">
                      <label>Settore/Progetto Unico</label>
                      <Select
                        value={
                          editFormData.settore
                            ? projectOptions.find(
                                (opt) => opt.value === editFormData.settore,
                              )
                            : null
                        }
                        onChange={(option) =>
                          handleInputChange({
                            target: {
                              name: "settore",
                              value: option ? option.value : "",
                            },
                          })
                        }
                        options={projectOptions}
                        placeholder="Seleziona un settore..."
                        isSearchable
                      />
                    </div>
                  ) : (
                    <div className="split-items-container">
                      <div className="split-header">
                        <span>Descrizione</span>
                        <span>Importo (€)</span>
                        <span>Settore</span>
                        <span>%</span>
                        <span></span>
                      </div>
                      {splitItems.map((item) => {
                        const rawTotal = Number(
                          editFormData.importo_totale || 0,
                        );
                        const docTotal = editFormData.scorporaIva
                          ? rawTotal / 1.22
                          : rawTotal;

                        const percent =
                          docTotal > 0
                            ? ((item.importo / docTotal) * 100).toFixed(1)
                            : 0;

                        return (
                          <div key={item.id} className="split-item-row">
                            <label className="mobile-label">Descrizione</label>
                            <input
                              type="text"
                              placeholder="Materiale/Voce"
                              value={item.descrizione}
                              onChange={(e) =>
                                updateSplitItem(
                                  item.id,
                                  "descrizione",
                                  e.target.value,
                                )
                              }
                            />
                            <label className="mobile-label">Importo (€)</label>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={item.importo}
                              onChange={(e) =>
                                updateSplitItem(
                                  item.id,
                                  "importo",
                                  e.target.value,
                                )
                              }
                            />
                            <label className="mobile-label">Settore</label>
                            <Select
                              value={
                                item.settore
                                  ? projectOptions.find(
                                      (opt) => opt.value === item.settore,
                                    )
                                  : null
                              }
                              onChange={(option) =>
                                updateSplitItem(
                                  item.id,
                                  "settore",
                                  option ? option.value : "",
                                )
                              }
                              options={projectOptions}
                              placeholder="Settore..."
                              className="split-select"
                            />
                            <span className="split-percent">{percent}%</span>
                            <button
                              className="btn-remove-item"
                              onClick={() => removeSplitItem(item.id)}
                            >
                              &times;
                            </button>
                          </div>
                        );
                      })}
                      <button className="btn-add-item" onClick={addSplitItem}>
                        + Aggiungi Riga
                      </button>

                      <div
                        className="vat-option"
                        style={{
                          margin: "10px 0",
                          display: "flex",
                          gap: "10px",
                        }}
                      >
                        <label
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <input
                            type="checkbox"
                            name="scorporaIva"
                            checked={editFormData.scorporaIva || false}
                            onChange={handleInputChange}
                            style={{ marginRight: "8px" }}
                          />
                          Considera IVA 22% inclusa nel totale (scorpora per
                          calcoli)
                        </label>
                      </div>

                      <div className="split-summary">
                        <p>
                          Totale Documento: €{" "}
                          {Number(editFormData.importo_totale || 0).toFixed(2)}
                        </p>
                        {editFormData.scorporaIva && (
                          <p style={{ color: "#666", fontSize: "0.9em" }}>
                            Totale Imponibile (senza IVA): €{" "}
                            {(
                              Number(editFormData.importo_totale || 0) / 1.22
                            ).toFixed(2)}
                          </p>
                        )}
                        <p>
                          Totale Assegnato: € {calculateTotalSplit().toFixed(2)}
                        </p>
                        <p
                          className={
                            Math.abs(
                              (editFormData.scorporaIva
                                ? Number(editFormData.importo_totale || 0) /
                                  1.22
                                : Number(editFormData.importo_totale || 0)) -
                                calculateTotalSplit(),
                            ) < 0.01
                              ? "status-ok"
                              : "status-warning"
                          }
                        >
                          Rimanente: €{" "}
                          {(
                            (editFormData.scorporaIva
                              ? Number(editFormData.importo_totale || 0) / 1.22
                              : Number(editFormData.importo_totale || 0)) -
                            calculateTotalSplit()
                          ).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="form-group">
                  <label>Settore/Progetto</label>
                  <Select
                    value={
                      editFormData.settore
                        ? projectOptions.find(
                            (opt) => opt.value === editFormData.settore,
                          )
                        : null
                    }
                    onChange={(option) =>
                      handleInputChange({
                        target: {
                          name: "settore",
                          value: option ? option.value : "",
                        },
                      })
                    }
                    options={projectOptions}
                    placeholder="Seleziona un settore..."
                    isSearchable
                  />
                </div>
              )}

              <div className="form-group">
                <label>Note Aggiuntive</label>
                <textarea
                  name="note"
                  value={editFormData.note || ""}
                  onChange={handleInputChange}
                  placeholder="Inserisci note manuali qui..."
                />
              </div>

              <div className="gd-form-actions">
                <button className="gd-btn-delete" onClick={handleDelete}>
                  Elimina
                </button>
                <button className="gd-btn-cancel" onClick={handleCloseModal}>
                  Annulla
                </button>
                {selectedType === "bolla_acquisto" && materialiFieldKey && (
                  <button
                    className="gd-btn-save gd-btn-save-materiali"
                    onClick={handleSaveMaterialiToRegistroAcquisti}
                    disabled={
                      responseRegistroAcquisti.isPending ||
                      isSavingRegistroMateriali
                    }
                    title="Salva tutti i materiali nel registro acquisti"
                  >
                    💾 Salva Materiali
                  </button>
                )}
                <button className="gd-btn-save" onClick={handleSave}>
                  Salva Modifiche
                </button>
                {selectedType === "bolla_vendita" && (
                  <button
                    className="gd-btn-save"
                    onClick={handleSaveDocumentoRicavo}
                    disabled={isSavingRicavo || isLoadingRicavoLink}
                  >
                    {isSavingRicavo
                      ? "Salvataggio..."
                      : linkedRicavoId
                        ? "Salva + aggiorna Ricavo"
                        : "Salva + crea Ricavo"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox per immagine a schermo intero */}
      {showFullImage && selectedDoc && (
        <div
          className="lightbox-overlay"
          onClick={() => setShowFullImage(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
            cursor: "zoom-out",
          }}
        >
          <img
            src={selectedDoc.fotoUrl}
            alt="Ingrandimento"
            style={{
              maxWidth: "95%",
              maxHeight: "95%",
              objectFit: "contain",
              boxShadow: "0 0 20px rgba(0,0,0,0.5)",
            }}
          />
          <button
            onClick={() => setShowFullImage(false)}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "2rem",
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
